// l2b-exercise.js — Language-to-Body training screen.
//
// Adapted from KFE Web's <kfe-l2b-exercise> (Brief 003). The L2BEngine wiring,
// render, feedback, large-intro, atomic-tile-renewal (Brief 134) and audio-guard
// logic are kept the same; the only swaps are the data plumbing — KFE's
// `services` / `mmu-loader` / sql.js calls become the demo's
// `demo-content-provider` + `learner-store` + the `audio` singleton — plus
// inlined English UI strings (i18n is Brief 004) and an inline completion view
// (KFE's shared <kfe-completion-screen> isn't ported to the demo).
//
// Audio rules (CLAUDE.md pitfall #3): the first play() must fire inside a user
// gesture, so we render a Start button and only call engine.start() — which
// drives the first audio — on its click. A `_lastAudioConceptId` guard mirrors
// the Unity port's replay-suppression fix.
//
// Reactive-prop landmine (Brief 001 handover #1 / Brief 003 §6): `itemId` is
// declared ONLY via `static props` and read with a `(this).itemId` cast. NEVER
// add a `itemId;` instance field — it clobbers KFEComponent's reactive accessor
// and the screen renders blank with no console error.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { audio } from "../core/audio-engine.js";
import { track, trackBeacon, embedSrc } from "../core/analytics.js";
import * as lang from "../i18n/language-manager.js";
import {
  L2BEngine,
  applyL2bDefensiveFilters,
  mmusToItemData,
} from "../engines/l2b-engine.js";
import { l2bModeRenderFlags } from "../data/mmu-loader.js";
import { resolveImage } from "../data/media-paths.js";
import { loadContent, getExercise } from "../data/demo-content-provider.js";
import { prepareCard } from "../ui/record-card.js";
import * as learner from "../data/learner-store.js";

const FEEDBACK_MS = 900;
const DEFAULT_MASTERY_STREAK = 3;

// Brief 011: on finishing Lesson 1 the completion state offers a "Continue" CTA
// that navigates the TOP window (escaping the embed iframe) to SpeechGenie. A
// cross-origin iframe can't set window.top.location programmatically, but a
// USER-ACTIVATED <a target="_top"> click is allowed (Gotcha 3) — so this is a
// real anchor the learner taps, never an auto-redirect. Single source of truth:
// repoint here. Empty string → fall back to "practice again" replay only.
const COMPLETION_URL = "https://speechgenie.co/kickstarter/";

// Brief 020b — the skipped ending's two exits. Same top-window escape as
// COMPLETION_URL (a user-activated <a target="_top">), so a learner who
// declines the email still leaves with somewhere to go.
const FULL_DEMO_URL = "https://speechgenie.co/full-demo/";
const HOW_IT_WORKS_URL = "https://speechgenie.co/how-speech-genie-works/";

// ─── Brief 021: the shareable record card ───────────────────────────────────
//
// Where the share sends a newcomer. NOT the Kickstarter and NOT the full demo:
// the card's whole claim is "I understood these", and the only honest landing
// for it is the page where a stranger can try the same six for themselves. The
// card's own mark prints the same address without the scheme.
const SHARE_URL = "https://speechgenie.co/l2b-demo/";

// The share sheet shows this to the sharer as the file's name, and it is what
// the desktop fallback saves. No run id, no token, no date — the card is fixed,
// so the filename is too.
const CARD_FILENAME = "speechgenie-l2b-demo.png";

// ─── Brief 020c: THE ONE CALL SITE, now armed ───────────────────────────────
//
// 020b left this inert because the endpoint was not pinned and the plan was a
// `no-cors`, fire-and-forget POST — write-only by construction, so `response.ok`
// was unreadable and a typo was indistinguishable from success. The plugin
// session pinned the route AND built read-back (their v0.6.7), so the POST is
// now a real CORS request whose result decides which panel state the learner
// sees. Nobody is told "check your inbox" over a failure.
//
// THE REQUEST MUST STAY A CORS *SIMPLE REQUEST*. `URLSearchParams` as the body
// sets `Content-Type: application/x-www-form-urlencoded` on its own, which is
// one of the three types a simple request may carry — so no preflight. Adding
// ANY custom header (or a JSON body, or `credentials`) triggers an OPTIONS
// preflight that v0.6.7 deliberately does not answer for this route, and the
// whole thing fails as an opaque "CORS error". Do not add headers here.
//
// NO `credentials`. The endpoint sets no `Access-Control-Allow-Credentials`, so
// sending them makes the browser reject an otherwise-valid response.
const DEFAULT_SIGNUP_ENDPOINT = "https://speechgenie.co/wp-json/speechgenie/v1/waitlist";

/**
 * Brief 020c — where the signup POST goes.
 *
 * PREVIEW BUILDS ONLY may repoint it with `?signup_endpoint=<url>`, because the
 * plugin is validated on a LocalWP install first and its Live Link subdomain is
 * ephemeral — a baked build var would mean a rebuild per session. In a
 * production build `isPreview` is the constant `false`, so this returns the
 * default before it ever looks at the query string.
 *
 * Exported for the tests, which need to assert BOTH halves: that a preview build
 * honours the param and that a production build ignores it. That second half is
 * the one that matters — an override honoured in production would let any link
 * with a query string redirect a stranger's address to somebody else's server.
 *
 * @param {string} search  `location.search`
 * @param {boolean} isPreview
 * @returns {string}
 */
export function resolveSignupEndpoint(search, isPreview) {
  if (!isPreview) return DEFAULT_SIGNUP_ENDPOINT;
  try {
    const raw = new URLSearchParams(search).get("signup_endpoint");
    if (!raw) return DEFAULT_SIGNUP_ENDPOINT;
    const url = new URL(raw);
    // http/https only: a `javascript:` or `data:` value would be a self-inflicted
    // injection even in a preview build.
    if (url.protocol !== "https:" && url.protocol !== "http:") return DEFAULT_SIGNUP_ENDPOINT;
    return url.href;
  } catch {
    return DEFAULT_SIGNUP_ENDPOINT; // unparseable param — fall back, never throw
  }
}

/**
 * Resolved once at load, like the query-string flags the screen already reads.
 *
 * `__L2B_PREVIEW__` is substituted at bundle time (build.mjs) and is tested
 * INLINE here, in the condition itself, rather than through a named constant:
 * that is what lets esbuild fold the whole branch away, so a production bundle
 * contains no call to the resolver and the string "signup_endpoint" does not
 * appear in the shipped JS at all. Ignoring the param would have satisfied the
 * brief; not shipping the code that reads it is better. (Verified against
 * `dist/` — a plain `npm run build` has zero occurrences.)
 *
 * Unbundled dev leaves the identifier undeclared, and `typeof` is the one safe
 * way to test for that without a ReferenceError (the same guard app/version.js
 * uses); it resolves to "not a preview", i.e. no override — the safe default.
 */
const SIGNUP_ENDPOINT = (typeof __L2B_PREVIEW__ !== "undefined" && __L2B_PREVIEW__)
  ? resolveSignupEndpoint(typeof location !== "undefined" ? location.search : "", true)
  : DEFAULT_SIGNUP_ENDPOINT;

// ─── Brief 026: attribution on the signup POST ──────────────────────────────
//
// The plugin's own attribution (a first-touch cookie, Stripe metadata) cannot
// reach this request: the POST is cross-site and deliberately credential-less
// (see the block above), so no cookie rides on it. Whatever the plugin is to
// know about where a demo signup came from, we have to put in the body.
//
// TWO RULES, AND THEY POINT OPPOSITE WAYS — both on purpose:
//   - every attribution field is OMITTED when it has no value. Never `""`,
//     never `"none"`: the plugin records on every provision and reads absence
//     as absence, so an empty string is a false "we checked, it was blank".
//   - `met_count` and `mastered_count` are ALWAYS sent, including 0. Their
//     presence is how the plugin tells a demo signup from any other, and 0 is
//     "opened it and did nothing" — a different funnel problem from "never came
//     through the demo". Dropping them at zero collapses the two.

/** The five UTM keys, read from OUR query string (the shortcode forwards them). */
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/** Cap per attribution value — the plugin stores them, so they are bounded here. */
const ATTR_MAX = 100;

/**
 * One attribution value made safe to send, or null. Printable only (control
 * characters stripped), trimmed, capped at `ATTR_MAX`, and null when nothing is
 * left — so the caller can omit it rather than send `""`.
 * @param {unknown} v
 * @returns {string | null}
 */
function cleanAttr(v) {
  if (typeof v !== "string") return null;
  const out = v.replace(/[\u0000-\u001F\u007F-\u009F]/g, "").trim().slice(0, ATTR_MAX).trim();
  return out || null;
}

/**
 * Brief 026a — the referrer reduced to origin + path — no query, no hash. The
 * plugin stores the cookie-path referrer the same way, so both doors write one
 * shape into `_sg_attr_referrer`. It also keeps the value clear of the
 * 100-character cap, which would otherwise truncate a real URL mid-query into a
 * non-URL.
 * @param {unknown} v
 * @returns {string | null}
 */
function refOriginPath(v) {
  if (typeof v !== "string" || !v) return null;
  try {
    const u = new URL(v);
    return cleanAttr(u.origin + u.pathname);
  } catch {
    return null; // not a URL — send nothing rather than a fragment of one
  }
}

/**
 * The UTMs present in `search`, cleaned; absent keys are simply not in the
 * result. Same defensive shape as `_embedSrc` in analytics.js: a query string is
 * attacker-supplied, so validate, and fall back to "nothing" rather than throw.
 * @param {string} search  `location.search`
 * @returns {Record<string, string>}
 */
export function readUtms(search) {
  /** @type {Record<string, string>} */
  const out = {};
  try {
    const q = new URLSearchParams(search);
    for (const k of UTM_KEYS) {
      const v = cleanAttr(q.get(k));
      if (v) out[k] = v;
    }
  } catch { /* unparseable — send no UTMs, never throw */ }
  return out;
}

/**
 * Resolved ONCE at load, like `_embedSrc`, and for a reason of this screen's
 * own: the router `pushState`s, so `location.search` at the moment of signup
 * need not be the query string the iframe was loaded with. The load is what
 * the shortcode tagged. `location` is undeclared under the node tests.
 */
const _utms = readUtms(typeof location !== "undefined" ? location.search : "");

/**
 * Brief 026 — the waitlist POST body. Pure, so the tests can pin the two rules
 * above without a DOM or a network.
 *
 * @param {{
 *   email: string, langCode: string, hp: string,
 *   utms?: Record<string, string>, referrer?: unknown, embedSrc?: unknown,
 *   metCount: number, masteredCount: number,
 * }} f
 * @returns {URLSearchParams}
 */
export function buildSignupBody(f) {
  const body = new URLSearchParams({ email: f.email, source: "l2b_demo", lang: f.langCode, hp: f.hp });
  for (const k of UTM_KEYS) {
    const v = cleanAttr(f.utms?.[k]);
    if (v) body.set(k, v);
  }
  // Inside an iframe the referrer is the embedding page — or empty, under a
  // strict referrer policy or a direct load. Empty means omit.
  const ref = refOriginPath(f.referrer);
  if (ref) body.set("ref", ref);
  const src = cleanAttr(f.embedSrc);
  if (src) body.set("embed_src", src);
  // ALWAYS, zero included — see the block comment above.
  body.set("met_count", String(countOrZero(f.metCount)));
  body.set("mastered_count", String(countOrZero(f.masteredCount)));
  return body;
}

/** @param {unknown} n @returns {number} */
function countOrZero(n) {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** `document.referrer`, or "" where reading it is impossible. @returns {string} */
function readReferrer() {
  try {
    return typeof document !== "undefined" ? document.referrer : "";
  } catch {
    return "";
  }
}

/**
 * @typedef {{ ok: true }
 *   | { ok: false, reason: "invalid" | "throttle" | "network", code: string }} SignupResult
 */

/**
 * Brief 020c — hand the learner's email to the waitlist endpoint AND READ THE
 * ANSWER. The caller awaits this and branches on it: that is the whole point of
 * the change from 020b, whose fire-and-forget POST could not tell a typo from a
 * success.
 *
 * NO PII BEYOND THIS FUNCTION. `email` is passed here and nowhere else — never a
 * GA4 event param, never logged, never put in a URL (house rule, and
 * `analytics.js` says the same from the other side). The `reason` this returns
 * is an enum precisely so the caller can report it to GA without carrying the
 * address along with it.
 *
 * WHAT MAPS TO WHAT (the plugin's contract):
 *   200        → `{ok:true}`; the magic link is on its way from support@.
 *   400        → the address was rejected. `reason: "invalid"` — stay on the
 *                prompt, keep the field, let them fix it.
 *   429        → throttled. `code` separates the two: `sg_waitlist_email_throttle`
 *                means THIS address already had its links this hour (a link is
 *                genuinely sitting in their inbox — effectively a success), and
 *                `sg_waitlist_throttle` is the per-IP cap (try again shortly).
 *   throw      → offline, DNS, blocked, CORS. `reason: "network"`.
 *   any other  → treated as `"network"`: an unexpected status is a request that
 *                did not get through, and "try again" is the honest thing to say.
 *
 * @param {string} email  already passed the caller's `@`-and-a-dot sanity check
 * @param {string} langCode  the learner's target language, e.g. "zh" — becomes
 *   the `L2B_Demo_{LANG}` tag server-side
 * @param {string} hp  the honeypot field's value. Empty from a real browser; a
 *   bot that fills it gets a normal-looking success and no account (server-side)
 * @param {{ metCount: number, masteredCount: number }} progress  how far they got
 *   (Brief 026) — the same counts `run_abandon` reports
 * @returns {Promise<SignupResult>}
 */
async function submitEmail(email, langCode, hp, progress) {
  // `URLSearchParams` → form-encoded body, no custom headers, no credentials:
  // a simple request that never preflights. See the block comment above.
  // Brief 026's extra fields are more form fields, which cost no preflight.
  const body = buildSignupBody({
    email, langCode, hp,
    utms: _utms,
    referrer: readReferrer(),
    embedSrc: embedSrc(),
    metCount: progress.metCount,
    masteredCount: progress.masteredCount,
  });
  let r;
  try {
    r = await fetch(SIGNUP_ENDPOINT, { method: "POST", body });
  } catch {
    return { ok: false, reason: "network", code: "" };
  }
  if (r.ok) return { ok: true };

  // The error body is best-effort: a WP REST error carries `{code, message}`,
  // but an interstitial or a proxy error page carries HTML, and failing to parse
  // it must not turn a readable 400 into an exception.
  let code = "";
  try {
    const parsed = await r.json();
    code = typeof parsed?.code === "string" ? parsed.code : "";
  } catch { /* leave `code` empty — the status alone still decides the state */ }

  if (r.status === 400) return { ok: false, reason: "invalid", code };
  if (r.status === 429) return { ok: false, reason: "throttle", code };
  return { ok: false, reason: "network", code };
}

/**
 * Brief 020c — which sentence the prompt shows for each failure. Kept as data
 * next to the endpoint rather than inline in the render, so the four cases are
 * visible as a set: they must stay four DIFFERENT sentences (a rejected address,
 * a malformed one, a rate limit and a dead network are four different next
 * actions), and none of them may say "check your inbox".
 * @type {Record<"invalid"|"rejected"|"throttle"|"network", string>}
 */
const EMAIL_ERROR_KEYS = {
  invalid: "l2b.email.invalid",
  rejected: "l2b.email.error.rejected",
  throttle: "l2b.email.error.throttle",
  network: "l2b.email.error.network",
};

// Brief 020 — the reveal's list order. The run is interleaved (walk, door,
// stand up, WALK TO THE DOOR, window, WALK TO THE WINDOW); the reveal is not.
// It groups the atoms, rules a hairline, and ENDS ON THE TWO SENTENCES, so the
// payload hands off as a pair even though they appeared mid-run. Ruled by Chris
// 2026-09-09: do not make the two orders match. This array is the v3 design's
// order verbatim (SHARE-MODAL-l2b-v3.html) and is the build's default; ids the
// run carries that aren't named here still appear, in entry order, after the
// ones that are. Grouping itself is data-driven — `isLargeImage` is how the
// corpus already distinguishes an atom from a recombination.
const REVEAL_ORDER = [1319, 4231, 1323, 1322, 1328, 1329];

// Brief 022 — `run_abandon` fires on EVERY hide, not once per page load, and
// `_hideSeq` numbers them. 020's once-per-load flag worked exactly as written
// and that was the bug: a learner who glanced at another tab was recorded as an
// abandonment, with a payload frozen at the moment of the glance rather than at
// the moment they left. Now each hide carries the state as it was at that hide,
// so the HIGHEST `hide_seq` in a session is by definition the last thing that
// happened before the page went away for good.
//
// Module scope, not the instance, and for 020's reasons plus one more: the
// counter has to be monotonic across the whole PAGE LOAD. The screen remounts
// (the direction toggle remounts the route) and "Practice again" starts a new
// lesson but not a new load — an instance field would restart at 1 after either,
// and a session with two different `hide_seq: 1` rows breaks the whole analysis,
// which is "a `run_abandon` whose `hide_seq` is matched by a `run_resume` was
// not an abandonment". Deliberately never reset — see `_fireAbandon`.
let _hideSeq = 0;

// Brief 022 — one departure, one row. `pagehide` and `visibilitychange`→hidden
// BOTH fire on a genuine close, and both must stay wired (iOS Safari frequently
// gives only the former, desktop tab-switching only the latter), so without a
// guard a single close is two `run_abandon` rows.
//
// This latches for the whole HIDDEN PERIOD rather than for a time window. The
// brief specified a ~50ms timestamp check; a latch is the same idea made exact,
// and it is the version that holds where it matters. The pair is only ever
// separated by milliseconds on desktop, but iOS commonly delivers `pagehide`
// long after the page went hidden — the OS suspending a backgrounded tab — and
// a 50ms window would let that through as a second row. Which is the double
// count the guard exists to prevent, on the mobile traffic this instrument is
// mostly measuring.
//
// It cannot suppress a real second departure: becoming visible again is the
// only way to depart twice, and that is exactly where it is released
// (`_fireResume`). One hidden period, one `hide_seq`, one row.
let _hideLatched = false;

// Brief 024 — when the current hidden period began, as a `performance.now()`
// stamp. Differenced at `_fireResume` to send `hidden_ms`, which is what turns
// 022's pairing from binary into continuous: today we can say a hide was
// matched, but not whether the learner glanced at a notification for fifteen
// seconds or closed the laptop and chose to come back after lunch. Those are
// opposite findings about the same person, and the second is the strongest
// positive signal this instrument can produce.
//
// `performance.now()`, not `Date.now()`: monotonic. Wall-clock moves under NTP
// correction, a timezone change and a phone resyncing after sleep — which is
// precisely the scenario a long hide describes — and a negative or wildly
// inflated duration from a clock adjustment would be indistinguishable from
// real data.
//
// Module scope for `_hideSeq`'s reasons: two screen instances can coexist
// (019a) and an instance field would not survive a remount or "Practice again".
//
// It records the duration. It does NOT decide what a departure is: no
// threshold, no flag, no classification. Where the line between "glanced away"
// and "left and came back" falls is an analysis decision, and it belongs in SQL
// where it can be moved and applied retrospectively — not compiled into the
// collector where revising it needs a deploy and the data already gathered
// cannot be reinterpreted.
let _hiddenAt = 0;

/** @param {string|null} l2bType → L2BConfig.modality */
function modalityFor(l2bType) {
  if (l2bType === "Text only") return "textOnly";
  if (l2bType === "Audio and Text") return "audioAndText";
  return "audioOnly";
}

/**
 * Decode an image URL into the browser cache so the tile paints on the first
 * frame after the atomic swap (Brief 134 — eliminates the first-run blank-tile
 * window). Resolves even on error/timeout so one missing or slow file never
 * blocks the whole grid from appearing.
 * @param {string} url
 * @returns {Promise<void>}
 */
function preloadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    const done = () => resolve();
    img.onload = done;
    img.onerror = done;
    img.src = url;
  });
}

export class L2BExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: min(720px, 100vw - 32px); margin-inline: auto; }
    h2 { margin-top: 0; color: var(--l2b-fg); text-shadow: 0 1px 3px rgba(0,0,0,0.6); }
    /* Target prompt framed in the dojo panel sprite (teal centre via slice fill). */
    .target { margin: 1rem 0; padding: 0.4rem 1rem; display: flex;
              align-items: center; gap: 1rem;
              border: var(--l2b-panel-border) solid transparent;
              border-image: var(--l2b-skin-panel) var(--l2b-panel-slice) / var(--l2b-panel-border) stretch;
              background: var(--l2b-surface); border-radius: var(--l2b-radius);
              filter: drop-shadow(0 4px 10px rgba(0,0,0,0.45)); }
    .target .word { font-size: var(--l2b-h2); flex: 1; color: var(--l2b-fg);
                    font-weight: 700; }
    .grid { margin-top: 1rem; }
    /* Primary action. On the dojo skin this rode the cream button sprite; on the
       embed (Brief 011) the skin tokens are unset, so the var() fallbacks take
       over: a solid white pill (--l2b-btn-bg) with blue text (--l2b-btn-fg) and a
       plain rounded border. The border-image line degrades to 'none' cleanly. */
    button.primary { padding: 0.7rem 1.5rem; color: var(--l2b-btn-fg, #4a2f1a);
                     font-weight: 800;
                     background: var(--l2b-btn-bg, transparent); cursor: pointer;
                     border-radius: var(--l2b-radius);
                     border: var(--l2b-btn-border, 0) solid transparent;
                     border-image: var(--l2b-skin-btn, none) var(--l2b-btn-slice, ) / var(--l2b-btn-border, 0) stretch;
                     filter: drop-shadow(0 3px 6px rgba(0,0,0,0.45));
                     transition: transform 0.1s ease, filter 0.1s ease; }
    button.primary:hover { filter: drop-shadow(0 5px 10px rgba(0,0,0,0.5)) brightness(1.04); }
    button.primary:active { transform: translateY(1px); }
    /* Replay = translucent gold-bordered pill led by the dojo volume icon. */
    button.replay { padding: 0.5rem 1rem 0.5rem 0.85rem; border-radius: 999px;
                    border: 1px solid var(--l2b-border); background: rgba(0,0,0,0.3);
                    color: var(--l2b-fg); cursor: pointer; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 0.4rem; }
    button.replay::before { content: ""; width: 1.1em; height: 1.1em; flex: none;
                    background: var(--l2b-skin-volume, none) center / contain no-repeat; }
    button.replay:hover { background: rgba(0,0,0,0.45); }
    /* Brief 006f: translation gated behind a small round "i" info button. The L1
       word is NOT shown by default; tapping ⓘ reveals it (and plays L1 audio),
       at which point the revealed pill is the existing .replay .translation. */
    button.translation-info { width: 2.2rem; height: 2.2rem; padding: 0;
                    border-radius: 999px; border: 1px solid var(--l2b-border);
                    background: rgba(0,0,0,0.3); color: var(--l2b-fg);
                    cursor: pointer; font-weight: 700; font-size: 1.4rem;
                    line-height: 1; font-style: italic;
                    display: inline-flex; align-items: center; justify-content: center; }
    button.translation-info:hover { background: rgba(0,0,0,0.45); }
    .large-intro { display: flex; flex-direction: column; align-items: center;
                   gap: 1rem; padding: 1.5rem 0; }
    /* Warm-paper card — L2B images are transparent-PNG line drawings designed for
       white-paper rendering; on the backdrop they'd vanish. */
    .large-intro img.l2b-image-container {
                       max-width: 80vw; max-height: 50vh; object-fit: contain;
                       border-radius: 12px; background: var(--l2b-image-bg);
                       padding: 1rem; box-sizing: border-box;
                       border: 3px solid var(--l2b-border);
                       box-shadow: 0 8px 24px rgba(0,0,0,0.5); }
    .large-intro .word { font-size: var(--l2b-h1); text-align: center;
                         color: var(--l2b-fg); font-weight: 800;
                         text-shadow: 0 1px 3px rgba(0,0,0,0.6); }
    .start-prompt { padding: 2rem; text-align: center; }
    /* Progress row: the dojo slider sprites (bg track + fill) stand in for the
       iOS KFEProgressLine / Unity progressBar pair. */
    .progress-row { display: flex; align-items: center; gap: 0.75rem;
                    margin-bottom: 0.75rem; }
    .progress-bar { flex: 1; height: 16px; border-radius: 8px; overflow: hidden;
                    background: var(--l2b-skin-slider-bg, none) center / 100% 100% no-repeat,
                                rgba(0,0,0,0.4); }
    .progress-fill { height: 100%; border-radius: 8px;
                     background: var(--l2b-skin-slider-fill, none) left center / cover no-repeat,
                                 var(--l2b-accent);
                     transition: width 0.3s ease-out; }
    .progress { font-size: 1.05rem; font-weight: 700; color: var(--l2b-fg);
                white-space: nowrap; text-shadow: 0 1px 2px rgba(0,0,0,0.6); }
    /* Feedback after a tap. The dojo skin hid the ✓/✗ glyph and showed a tick/fail
       sprite via ::before; with the skin gone (Brief 011) we just show the glyph
       itself, tinted to the success/error colour. */
    .feedback { padding: 0.75rem; border-radius: 12px; margin: 0.75rem 0;
                font-size: 2rem; text-align: center; line-height: 1;
                display: flex; align-items: center; justify-content: center; }
    .feedback.correct { background: rgba(31, 61, 31, 0.85); border: 2px solid var(--l2b-success);
                        color: var(--l2b-success); }
    .feedback.wrong   { background: rgba(61, 31, 31, 0.85); border: 2px solid var(--l2b-error);
                        color: var(--l2b-error); }
    /* Completion / not-found panel, framed in the dojo panel sprite. */
    .complete { padding: 1.5rem 2rem; text-align: center; margin: 1rem auto; max-width: 32rem;
                border: var(--l2b-panel-border) solid transparent;
                border-image: var(--l2b-skin-panel) var(--l2b-panel-slice) / var(--l2b-panel-border) stretch;
                background: var(--l2b-surface); border-radius: var(--l2b-radius);
                filter: drop-shadow(0 6px 18px rgba(0,0,0,0.5)); }
    .complete .headline { font-size: var(--l2b-h1); margin-bottom: 0.5rem;
                          color: var(--l2b-fg); font-weight: 800; }
    .complete .detail { color: var(--l2b-muted); margin-bottom: 1.5rem; }
    .complete .actions { display: flex; flex-direction: column; gap: 0.75rem;
                         align-items: center; }
    a.cta { display: inline-block; text-decoration: none;
            background: rgba(0,0,0,0.3); color: var(--l2b-fg);
            border: 1px solid var(--l2b-border);
            padding: 0.6rem 1.4rem; border-radius: var(--l2b-radius); font-weight: 700; }
    a.cta:hover { background: rgba(0,0,0,0.45); }
    /* The completion "Continue" CTA (Brief 011) is the primary exit — a filled
       accent pill so it reads as the main action above the "practice again" replay. */
    a.cta.continue { background: var(--l2b-accent); color: var(--l2b-on-accent);
                     border-color: transparent;
                     filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4)); }
    a.cta.continue:hover { background: var(--l2b-accent); filter: drop-shadow(0 5px 10px rgba(0,0,0,0.5)) brightness(1.05); }
    /* Brief 013 — the idle feedback slot. tileGrid renders this empty placeholder
       in place of the ✓/✗ box every non-feedback frame; on desktop it collapses
       to nothing (no margin/padding/height) so the layout is byte-identical to
       before. On short viewports the media query below gives it the active box's
       footprint so the grid + drop zone don't jump when ✓/✗ flips in. */
    .feedback.idle { margin: 0; padding: 0; min-height: 0;
                     background: transparent; border: 0; color: transparent; }
    /* Brief 013 — mobile/short-height compaction, guarded behind a short-height OR
       narrow-width query so a tall desktop window is untouched (success criterion
       5). The short desktop 16:10 inline frame (≈475px tall) and the phone
       full-screen tab both trip the height query, so both get the tighter rhythm
       that keeps progress + target + 4×2 grid + the whole drop zone on one screen. */
    @media (max-height: 720px), (max-width: 600px) {
      h2 { font-size: 1.1rem; margin-bottom: 0.25rem; }
      .target { margin: 0.5rem 0; }
      .progress-row { margin-bottom: 0.4rem; }
      .feedback { margin: 0.4rem 0; padding: 0.3rem; font-size: 1.5rem; }
      /* Reserve the feedback row so the grid/drop-zone don't shift when ✓/✗ flips
         in: the idle slot matches the active box's vertical footprint. */
      .feedback.idle { margin: 0.4rem 0; min-height: calc(1.5rem + 0.6rem); }
      .grid { margin-top: 0.5rem; }
    }

    /* Brief 013a — the largeIntro cap. The introduced item's image is bounded by
       max-height: 50vh, which is the right rule on a phone (where 50vh of a
       667px tab is a generous drawing and the screen still fits) but not in a
       SHORT box: at a 520px embed the image plus .large-intro's own 1.5rem
       padding and 1rem gaps overran <main> by ~56px, so the ⓘ / replay / ▶ row —
       the only affordance that moves the learner forward — sat below the fold on
       the very first screen of the run. Bounding the image to a fraction of the
       box (dvh, URL-bar-safe — Gotcha 3) and tightening the column's own rhythm
       puts that row back on screen. Gated on height ALONE and at 620px, below the
       phone's 667: a tall phone in portrait, which already fits, is byte-identical
       (success criterion 4); a tall desktop or an iPad in portrait keeps the 50vh
       drawing. Only a genuinely short box pays. */
    @media (max-height: 620px) {
      .large-intro { padding: 0.5rem 0; gap: 0.6rem; }
      .large-intro img.l2b-image-container { max-height: 38dvh; padding: 0.5rem; }
    }

    /* Brief 013a — the very-short tier. 013's compaction was tuned for a ~475px
       frame; the two boxes below that (the 440px WordPress embed as it ships
       today, and a phone held in landscape at ~390px) still overran by 23px and
       52px once the tile cap and the side-by-side reflow had done their work.
       This closes the last of it by spending the header rhythm rather than the
       playfield: every value here is a trim of margin, padding or reserved slot,
       nothing structural. The reserved feedback slot shrinks WITH the active box
       (both are retuned together) so 013's no-jump contract still holds. Height
       only, at 460px: the phone in portrait never reaches it. */
    @media (max-height: 460px) {
      h2 { font-size: 1rem; margin-bottom: 0.1rem; }
      .progress-row { margin-bottom: 0.3rem; }
      .target { margin: 0.35rem 0; padding: 0.3rem 0.8rem; }
      .feedback { margin: 0.25rem 0; padding: 0.2rem; font-size: 1.25rem; }
      .feedback.idle { margin: 0.25rem 0; min-height: calc(1.25rem + 0.4rem); }
      .grid { margin-top: 0.25rem; }
      .large-intro { padding: 0.25rem 0; gap: 0.45rem; }
      .large-intro img.l2b-image-container { max-height: 30dvh; padding: 0.4rem; }
    }

    /* ── Brief 020: the reveal ──────────────────────────────────────────────
       Built to SHARE-MODAL-l2b-v3.html's reveal pane, at the frame that
       actually ships. The WordPress embed is aspect-ratio 16/10, so on a tablet
       in portrait its height is about 440px — not the 700px phone frame — and
       018 removed the rotate guard, so a phone in landscape (~390px) is not
       avoidable either. Both are short and wide and get the same two-column
       reflow; a tall phone in portrait gets the same pane in one column.

       THE PINNING CONTRACT (criterion 4). The pane is a flex column that fills
       <main>'s row; .scrollpart is the ONLY scroller; .tail — the line and
       the buttons — is flex: none. So when the box is too short the six
       scroll and nothing else moves. :host(.l2b-reveal) takes the height ONLY
       in this state, so the training states keep their auto height and 013's
       mobile fit is untouched. If the 100% ever fails to resolve (no shell, a
       test harness) the pane simply flows at content height and <main> scrolls
       — degraded, not broken.

       No count, no percentage, no streak, no timer, no celebration: the
       double-take is the reward, and a number only invites comparison. */
    :host(.l2b-reveal) { height: 100%; }
    .reveal { display: flex; flex-direction: column; height: 100%; min-height: 0;
              max-width: 32rem; margin-inline: auto; }
    .reveal-cols { flex: 1; min-height: 0; display: flex; flex-direction: column; }
    .hero-wrap { flex: none; }
    .hero-row { display: flex; align-items: center; gap: 0.875rem; }
    .hero-zh { font-size: 2.5rem; line-height: 1.08; font-weight: 600;
               letter-spacing: 0.02em; color: var(--l2b-fg); }
    /* The tile image the learner has been dragging — a warm-white cell, because
       these are transparent-PNG line drawings that would vanish on the field. */
    .hero-im { width: 48px; height: 48px; flex: none; border-radius: 9px;
               background: var(--l2b-image-bg, #fff);
               display: grid; place-items: center; }
    .hero-im img { width: 94%; height: 94%; object-fit: contain; }
    .hero-claim { margin: 0.7rem 0 0; font-size: 1rem; color: var(--l2b-fg); }
    /* Tall box: the list sits centred in the space between the hero and the
       pinned tail (v3's own treatment) rather than stranded at the top with a
       hole under it. The short box overrides this to flex-start, where every
       pixel is spoken for. */
    .built { margin-top: 0.75rem; flex: 1; min-height: 0;
             display: flex; flex-direction: column; justify-content: center; }
    .built-label { font-size: 0.6875rem; letter-spacing: 0.1em;
                   text-transform: uppercase; color: var(--l2b-muted);
                   margin-bottom: 0.5rem; }
    .scrollpart { display: flex; flex-direction: column;
                  overflow-y: auto; min-height: 0; flex: 1; }
    .rowset { display: flex; flex-direction: column; gap: 3px; }
    .rowset .r { display: grid; grid-template-columns: 1fr auto;
                 align-items: center; gap: 0.75rem; }
    .rowset .zh { font-size: 1.1875rem; color: var(--l2b-fg); }
    /* The drawing, not the translation — the thing the sound was attached to.
       No English anywhere in the list: it is the whole point that nobody
       translated it (and the design's Drawings/English toggle is a review aid
       that must not ship). */
    .rowset .im { width: 40px; height: 40px; border-radius: 7px; justify-self: end;
                  background: var(--l2b-image-bg, #fff);
                  display: grid; place-items: center; }
    .rowset .im img { width: 94%; height: 94%; object-fit: contain; }
    /* The hairline: the four atoms above it, the two sentences below. */
    .rowset.made { border-top: 1px solid rgba(255, 255, 255, 0.20);
                   margin-top: 0.5rem; padding-top: 0.5rem; }
    .rowset.made .zh { font-size: 1.3125rem; font-weight: 600; }
    .rowset.made .im { width: 48px; height: 48px; }
    .tail { flex: none; margin-top: 0.75rem; }
    .tail .line { margin: 0; font-size: 1rem; color: var(--l2b-fg); }
    .reveal .actions { display: flex; flex-direction: column;
                       gap: 0.5625rem; margin-top: 0.875rem; }
    .reveal .actions > * { flex: 1; text-align: center; }
    .reveal .actions a.cta.continue { padding: 0.7rem 1.25rem; border-radius: 999px; }
    .reveal .actions button.ghost { cursor: pointer; font: inherit;
                       font-size: 0.9375rem; font-weight: 500;
                       padding: 0.7rem 1.25rem; border-radius: 999px;
                       border: 1px solid var(--l2b-border);
                       background: transparent; color: var(--l2b-fg); }
    .reveal .actions button.ghost:hover { background: rgba(255, 255, 255, 0.07); }

    /* ── Brief 021a: the share chooser ──────────────────────────────────────
       A small popover over the reveal, not a route. position:fixed so it
       covers the iframe viewport whether the demo is boxed (~440px) or in
       fullscreen — the backdrop is the whole surface and taps outside the card
       close it. High z so it sits over the pinned action row. */
    .share-overlay {
      position: fixed; inset: 0; z-index: 60;
      display: flex; align-items: center; justify-content: center;
      padding: 16px; background: rgba(0, 0, 0, 0.55);
    }
    .share-menu {
      position: relative; width: min(320px, 100%);
      max-height: 90%; overflow-y: auto;
      display: flex; flex-direction: column; gap: 0.625rem;
      padding: 1.25rem 1.125rem 1.125rem;
      background: var(--l2b-surface); color: var(--l2b-fg);
      border: 1px solid var(--l2b-border); border-radius: var(--l2b-radius);
      box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.7);
    }
    .share-menu-title {
      margin: 0 1.5rem 0.25rem 0; font-size: 1rem; font-weight: 600;
      color: var(--l2b-fg);
    }
    .share-menu-item {
      cursor: pointer; font: inherit; font-size: 0.9375rem; font-weight: 600;
      text-align: center; padding: 0.7rem 1rem; border-radius: 999px;
      border: 1px solid var(--l2b-border); background: rgba(0, 0, 0, 0.25);
      color: var(--l2b-fg);
    }
    .share-menu-item:hover { background: rgba(255, 255, 255, 0.08); }
    .share-menu-item:disabled { cursor: default; opacity: 0.7; }
    .share-menu-item:focus-visible,
    .share-menu-close:focus-visible {
      outline: 2px solid var(--l2b-accent); outline-offset: 2px;
    }
    .share-menu-close {
      position: absolute; top: 0.375rem; right: 0.5rem;
      cursor: pointer; width: 2rem; height: 2rem; line-height: 1;
      font: inherit; font-size: 1.5rem; border: 0; border-radius: 999px;
      background: transparent; color: var(--l2b-muted);
    }
    .share-menu-close:hover { color: var(--l2b-fg); }
    /* THE SHORT BOX — the ~440px embed and a phone in landscape. Hero pinned
       left, the six scrolling in their own column, line + buttons pinned across
       the bottom. grid-template-rows: minmax(0, 1fr) is load-bearing: it
       forces the row to the pane's height with a zero minimum, which is what
       lets .scrollpart bound and scroll instead of the row growing to fit.
       Guarded on width too — below ~560px two columns stop being readable, and
       the one-column pane already holds, because the list is the only scroller
       there as well. */
    @media (max-height: 560px) {
      .hero-zh { font-size: 2rem; }
      .hero-claim { font-size: 0.875rem; margin-top: 0.625rem; }
      .hero-im { width: 42px; height: 42px; }
      .built { margin-top: 0; justify-content: flex-start; }
      .built-label { margin-bottom: 0.3125rem; }
      .rowset { gap: 2px; }
      .rowset .zh { font-size: 0.96875rem; }
      /* 32px, not v3's 34px. Row height is image-driven, and at 34px the six
         overflowed the column by ~5px in the real 700x440 box — which reads as
         a clipped last row rather than as a scrollable list. 32px fits all six
         with room to spare at 440; the scroller below is still what carries a
         shorter box (a phone in landscape is ~390px and does scroll). */
      .rowset .im { width: 32px; height: 32px; }
      .rowset.made { margin-top: 0.4375rem; padding-top: 0.4375rem; }
      .rowset.made .zh { font-size: 1.0625rem; }
      .rowset.made .im { width: 38px; height: 38px; }
      .tail { margin-top: 0.5625rem; }
      .tail .line { font-size: 0.875rem; }
      .reveal .actions { flex-direction: row; margin-top: 0.5625rem; }
    }
    /* The two-column reflow itself needs width as well as shortness. Every box
       that actually ships has it — the 16/10 embed is ~700px wide at 440px
       tall, and the narrowest phone in landscape is 568px — so this is the
       normal short case, and the query below is the floor under it: a
       hand-narrowed short window keeps one column, still compacted, with the
       list scrolling under the pinned line and buttons. */
    @media (max-height: 560px) and (min-width: 480px) {
      .reveal { max-width: none; }
      .reveal-cols { display: grid; grid-template-columns: 1fr 1.15fr;
                     grid-template-rows: minmax(0, 1fr); gap: 0 1.625rem; }
      .reveal-cols > * { min-height: 0; }
    }

    /* ── Brief 020b: the email panel ────────────────────────────────────────
       The panel is a PANE, not a strip under the reveal — p-email /
       p-done / p-out in SHARE-MODAL-l2b-v3.html, which is also why all
       three states carry "See what you understood" back to the reveal. It
       reuses .reveal's own box (:host(.l2b-reveal) keeps the 100% height
       and the flex column) so THE PINNING CONTRACT IS THE SAME ONE, not a
       second implementation of it: .email-body is the only scroller and
       .tail is flex: none, so at 440px the copy scrolls and the buttons
       never move. .reveal .actions already flips column → row under
       max-height: 560px, and the panel inherits that flip for free.

       Deliberately NOT two columns: the short-box query below compacts the
       panel but adds no grid, because a headline plus a field plus two buttons
       reflowed into two columns reads as a form that lost its label. The
       reveal reflows because it has two genuinely separate things (the hero
       and the list); this has one. */
    /* justify-content is declared twice on purpose. Centred is right for the
       panel — unlike the reveal's list it is a short block, and top-aligning it
       leaves an obvious hole at 440px — but a CENTRED flex column that
       overflows its scroller clips at the TOP, where the headline is, and the
       clipped part cannot be scrolled back to. "safe center" is exactly that
       fix: centre while it fits, fall back to flow-start the moment it does
       not. Browsers that do not parse it drop the second declaration and keep
       the first, which is the pre-existing behaviour, not a broken one. */
    .email-body { flex: 1; min-height: 0; overflow-y: auto;
                  display: flex; flex-direction: column;
                  justify-content: center; justify-content: safe center;
                  text-align: center; }
    .email-big { margin: 0; font-size: 1.375rem; line-height: 1.25;
                 font-weight: 600; color: var(--l2b-fg); }
    .email-sub { margin: 0.625rem 0 0; font-size: 0.9375rem;
                 color: var(--l2b-muted); }
    /* The address in the registered state — v3 sets it apart so the learner
       knows what to look for (and what to un-junk) without the copy changing. */
    .email-addr { color: var(--l2b-fg); font-weight: 600; white-space: nowrap; }
    .email-field { margin: 1rem auto 0; display: block; width: 100%;
                   max-width: 20rem; font: inherit; font-size: 1rem;
                   padding: 0.65rem 0.9rem; border-radius: var(--l2b-radius);
                   border: 1px solid var(--l2b-border);
                   background: rgba(0, 0, 0, 0.3); color: var(--l2b-fg); }
    .email-field::placeholder { color: var(--l2b-muted); }
    .email-field:focus { outline: 2px solid var(--l2b-accent); outline-offset: 1px; }
    /* Shown only after a failed @-and-a-dot check, and it takes no layout
       when it is absent — an always-present slot would push the field up the
       pane at 440px for a message most learners never see. */
    .email-error { margin: 0.5rem 0 0; font-size: 0.8125rem; color: var(--l2b-error); }
    /* Brief 020c — the honeypot. Off-screen by POSITION, never display:none:
       the bots this is aimed at skip fields that are display:none or
       visibility:hidden, and fill everything else. It must also take no layout
       in the pane, hence position:absolute. */
    .email-hp { position: absolute; left: -9999px; width: 1px; height: 1px;
                opacity: 0; pointer-events: none; }
    /* Brief 020c — "Open the full demo", under the copy on both success panes.
       A quiet link, not a second CTA: the action that matters is the magic link
       waiting in their mail, and a filled pill here would compete with it.
       (The word this comment carefully does not use is asserted against by
       tests/screens/email-panel.test.mjs — the shadow root's textContent
       includes this stylesheet, comments and all.) */
    .email-link { display: inline-block; margin: 0.875rem auto 0;
                  font-size: 0.875rem; color: var(--l2b-muted);
                  text-decoration: underline; text-underline-offset: 3px; }
    .email-link:hover { color: var(--l2b-fg); }
    /* In flight: Register says "Sending…" and stops taking taps. */
    .reveal .actions button.cta:disabled { cursor: default; opacity: 0.6;
                     filter: none; }
    .email-field:disabled { opacity: 0.7; }
    /* The reveal's CTA is an <a target="_top">; the panel's primary is a real
       <button> (it advances a state, it does not navigate), so the filled-pill
       treatment needs the button selector too. Scoped under .reveal so the
       training states cannot see it. */
    .reveal .actions button.cta { display: inline-block; cursor: pointer;
                     font: inherit; font-weight: 700; text-align: center;
                     padding: 0.7rem 1.25rem; border-radius: 999px;
                     border: 1px solid var(--l2b-border);
                     background: rgba(0, 0, 0, 0.3); color: var(--l2b-fg); }
    .reveal .actions button.cta.continue { background: var(--l2b-accent);
                     color: var(--l2b-on-accent); border-color: transparent;
                     filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4)); }
    .reveal .actions button.cta.continue:hover { filter:
                     drop-shadow(0 5px 10px rgba(0,0,0,0.5)) brightness(1.05); }
    /* "See what you understood" — a quiet text link, never a third button.
       It is a way back to the payload, not a competing action. */
    .email-back { display: block; margin: 0.75rem auto 0; cursor: pointer;
                  font: inherit; font-size: 0.875rem; padding: 0.25rem;
                  border: 0; background: none; color: var(--l2b-muted);
                  text-decoration: underline; text-underline-offset: 3px; }
    .email-back:hover { color: var(--l2b-fg); }
    /* The skipped ending's exits are anchors (target="_top"), so they need
       the ghost/pill treatment 020 wrote for buttons. Separate rules rather
       than a comma bolted onto 020's, so that line stays as it shipped. */
    .reveal .actions a.ghost { display: inline-block; text-decoration: none;
                     cursor: pointer; font: inherit; font-size: 0.9375rem;
                     font-weight: 500; padding: 0.7rem 1.25rem;
                     border-radius: 999px; border: 1px solid var(--l2b-border);
                     background: transparent; color: var(--l2b-fg); }
    .reveal .actions a.ghost:hover { background: rgba(255, 255, 255, 0.07); }
    @media (max-height: 560px) {
      .email-body { padding-top: 0.25rem; }
      .email-big { font-size: 1.125rem; }
      .email-sub { margin-top: 0.4375rem; font-size: 0.875rem; }
      .email-field { margin-top: 0.625rem; padding: 0.5rem 0.8rem; }
      .email-back { margin-top: 0.5rem; font-size: 0.8125rem; }
    }

    /* ── Brief 021: Share on the reveal's action row ────────────────────────
       ONE control, and it is the ghost one. The pinned row already carries
       [Continue] and [Practice again] inside the 013a ~440px budget, and
       criterion 4 says the row does not move — so Share may not add a pixel to
       the tail's height in the short box, and may not outweigh Continue
       anywhere. It reuses the .ghost pill wholesale (no new treatment) and adds
       nothing but the two declarations below.

       The visible word is "Share"; the aria-label carries the full "Share what
       you understood" (see _shareControl). At 440px the row is three flex:1
       columns of roughly 220px, and the long label wraps to two lines there —
       which grows the tail, i.e. the one thing the budget cannot spend. The
       compact word plus a real accessible name costs a sighted learner nothing
       and costs a screen-reader user nothing either.

       THIS BLOCK IS LAST IN THE SHEET ON PURPOSE. 020's max-height: 560px
       block sets the column-to-row flip, and 020b's .reveal .actions
       button.cta rules come AFTER it at equal specificity — so a padding trim
       written into 020's block would simply be overridden. Same specificity,
       later in the sheet, wins. */
    @media (max-height: 560px) {
      /* Two pills' worth of padding for three. The labels are short; the 1.25rem
         was sized for a two-button row, and at 390px wide (a phone in landscape,
         which 018 made reachable) three of them at that padding overrun. */
      .reveal .actions button.cta,
      .reveal .actions button.ghost,
      .reveal .actions a.ghost,
      .reveal .actions a.cta.continue {
        padding-inline: 0.6rem;
        /* A wrapped label is a taller tail, and the tail is pinned. Never wrap. */
        white-space: nowrap;
      }
    }
  `;

  /** @type {L2BEngine | null} */
  engine = null;

  constructor() {
    super();
    this._started = false;
    /** @type {string|null} */ this._lastAudioConceptId = null;
    /** @type {string|null} */ this._lastMode = null;
    /** @type {string|null} */ this._largeIntroImageUrl = null;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._feedbackTimer = null;
    /** @type {number[]} */ this._allItemMmuIds = [];
    this._notFound = false;
    // Brief 003a: set when every MMU in the lesson is already mastered, so the
    // active Training pool is empty. Renders the "already complete" panel
    // instead of building an engine.
    this._allMastered = false;
    // Brief 101: per-mode rendering flags. Brief 006e: the demo presents the
    // target audio-only — the learner hears the word + sees the image but does
    // not read it (core L2B: sound → embodied meaning, no reading crutch). So we
    // pin the demo L2BType to "Audio only" → { showText: false, autoPlay: true }.
    /** @type {string|null} */ this._l2bType = "Audio only";
    this._renderFlags = { showText: false, autoPlay: true };
    // Brief 006f: the translation aid is hidden behind an "i" info button. This
    // per-item flag is false (ⓘ-only) until the learner taps to reveal; reset
    // each time a new largeIntro item appears (_afterStateChange) so a revealed
    // translation never carries into the next MMU. NOT a `static props` name
    // (reactive-prop landmine — Gotcha 5).
    this._translationRevealed = false;
    // Brief 134: atomic tile renewal. `_tileUrls` is the pre-resolved
    // { mmuId → imageUrl } map handed to <kfe-l2b-drag-area> so the grid paints
    // images on its first frame. `_prepareToken` guards against a stale
    // pre-resolution finishing after a newer state superseded it.
    /** @type {Record<number,string>} */ this._tileUrls = {};
    this._prepareToken = 0;
    /** @type {(() => void) | null} */ this._langOff = null;
    // Brief 016 (GA4): lesson-scoped analytics state. `_lessonCompleteTracked`
    // dedupes `lesson_complete` (the complete state re-publishes on every
    // stateChanged/render); `_attemptCount` is the round number carried on
    // `item_attempt`; `_streaks` mirrors the engine's per-item consecutive-
    // correct count so the SCREEN — not the engine (Rule #1) — can tell when an
    // item crosses the mastery streak, and `_masteredTracked` fires that once
    // per item per lesson. All reset by "Practice again", which starts a new
    // lesson attempt. `lesson_start` needs no flag of its own: `_started`
    // already guards the one place it fires.
    this._lessonCompleteTracked = false;
    this._attemptCount = 0;
    /** @type {Map<string, number>} */ this._streaks = new Map();
    /** @type {Set<string>} */ this._masteredTracked = new Set();
    // Brief 020: the reveal. `_exerciseMmus` is the lesson's full run (the
    // curated six) kept in ENTRY order — the engine pool can be a subset (003a
    // filters out already-mastered items) but the reveal shows what the learner
    // understood, so it reads the lesson, not the pool. `_revealUrls` is the
    // mmuId → image map the pane paints from, seeded from the run's own
    // already-decoded tiles so the pane lands with drawings on its first frame;
    // `_revealUrlsToken` drops a stale resolve. `_revealMounted` is the abandon
    // stage; `_revealTracked` dedupes `reveal_view` the way
    // `_lessonCompleteTracked` dedupes `lesson_complete` (the complete state can
    // publish more than once — a late feedback timer, a re-render).
    /** @type {import("../data/demo-content-provider.js").Exercise["mmus"]} */
    this._exerciseMmus = [];
    /** @type {Record<number,string>} */ this._revealUrls = {};
    this._revealUrlsToken = 0;
    this._revealMounted = false;
    this._revealTracked = false;
    // Brief 020b — the email panel's screen state. `null` means the reveal
    // itself is on screen; the three panel states replace it in the same pane
    // and every one of them offers a way back to `null`. `_emailValue` survives
    // the re-render a state change causes, so a mistyped address is still there
    // to correct rather than being silently wiped. `_emailViewTracked` dedupes
    // `email_view` exactly as `_revealTracked` dedupes `reveal_view` — walking
    // back to the reveal and forward again is one learner seeing one panel.
    // Brief 020c adds `"already"` — the 429 `sg_waitlist_email_throttle` ending.
    // It is a SUCCESS pane, not an error one: that code means this address has
    // already had its links this hour, so a magic link is genuinely sitting in
    // their inbox and the only useful thing to say is "look there, and in spam".
    /** @type {null | "prompt" | "registered" | "already" | "skipped"} */
    this._emailState = null;
    this._emailValue = "";
    // Brief 020c: an enum, not a boolean — the prompt now shows four different
    // messages (a client-side malformed address, a server 400, the per-IP 429,
    // and a request that never arrived) and they are not the same sentence.
    /** @type {null | "invalid" | "rejected" | "throttle" | "network"} */
    this._emailError = null;
    // Brief 020c: the honeypot's value, mirrored off the input the same way
    // `_emailValue` is, so a re-render mid-flight cannot lose it. A real browser
    // leaves it empty; a bot that fills it gets a normal-looking success and no
    // account (the server drops it — telling a bot it was caught only teaches
    // whoever wrote it to stop filling the field in).
    this._hpValue = "";
    // Brief 020c: the POST is awaited now, so there IS an in-flight window. It
    // disables Register — a second submit inside it would spend one of the three
    // links this address gets per hour and land the learner on the throttle
    // ending for no reason.
    this._emailPending = false;
    this._emailViewTracked = false;
    // Brief 021 — the shareable record card, in three pieces of state.
    //
    // `_cardPrep` is the whole card in flight — decode, composite AND PNG
    // encode — started when the reveal mounts. Measured, the encode alone is
    // about a second (see `prepareCard`), which is not something a click should
    // carry: `navigator.share()` has to be reached from inside the gesture on
    // iOS, so by the time it is tapped there must be nothing left to wait for.
    //
    // `_cardBlob` is that PNG once the screen has taken delivery of it. It is
    // never rebuilt — the card is fixed, so a second Share is the same bytes —
    // and it survives "Practice again" deliberately (see `_onPractiseAgain`).
    //
    // `_cardTracked` is `record_generate`'s once-per-page-load flag, the same
    // shape as `_revealTracked` / `_emailViewTracked`.
    /** @type {Promise<Blob> | null} */
    this._cardPrep = null;
    /** @type {Blob | null} */ this._cardBlob = null;
    this._cardTracked = false;

    // Brief 021a — the share chooser. `_shareMenuEl` is the open popover (null
    // when closed); `_shareMenuTrigger` is the reveal button to return focus to;
    // `_shareMenuKeydown` is the Esc/Tab handler, removed on close so it never
    // outlives the menu.
    /** @type {HTMLElement | null} */ this._shareMenuEl = null;
    /** @type {HTMLElement | null} */ this._shareMenuTrigger = null;
    /** @type {((e: KeyboardEvent) => void) | null} */ this._shareMenuKeydown = null;
    // Brief 020 (`run_abandon`): how far in they were when the page went away.
    // `_preMastered` is how many of the six 003a filtered out as already
    // mastered BEFORE this run — added to the run-scoped mirrors so `met_count`
    // and `mastered_count` are always out of the six a stranger meets, not out
    // of whatever subset the engine happened to be given. `_metIds` mirrors the
    // engine's met universe screen-side (Rule #1 — the engine tells nobody).
    // `_startedAt` is a `performance.now()` stamp, monotonic and immune to a
    // clock change mid-run; 0 means Start was never pressed.
    this._preMastered = 0;
    /** @type {Set<string>} */ this._metIds = new Set();
    this._startedAt = 0;
    /** @type {number|null} */ this._firstAttemptMs = null;
    // Whether PROMPT audio has actually played this run — the one boolean that
    // separates "the demo was silent and therefore unplayable" from "they
    // shrugged". `_promptAudioPending` narrows it to the target prompt: the ⓘ
    // affordance plays L1 audio through the same element, and a learner who
    // only ever heard the translation never heard the thing to drag.
    this._audioOk = false;
    this._promptAudioPending = false;
    /** @type {(() => void) | null} */ this._audioOff = null;
    /** @type {(() => void) | null} */ this._abandonOff = null;
  }

  async onConnected() {
    await loadContent();
    // Re-render labels (target word + translation) when the L1 changes. Guarded
    // + addCleanup so re-entry via "Practice again" never stacks handlers
    // (Gotcha 4 — onConnected is re-invoked without a disconnect).
    if (!this._langOff) {
      this._langOff = lang.onLanguageChanged(() => this._scheduleRender());
      this.addCleanup(() => { this._langOff?.(); this._langOff = null; });
    }
    this._installAudioProbe();
    this._installAbandonListeners();
    this._notFound = false;
    this._allMastered = false;
    const itemId = Number(/** @type {any} */ (this).itemId);
    const ex = Number.isFinite(itemId) ? getExercise(itemId) : null;
    if (!ex || ex.mmus.length === 0) {
      this._notFound = true;
      this._scheduleRender();
      return;
    }

    this._l2bType = "Audio only";
    this._renderFlags = l2bModeRenderFlags(this._l2bType);

    const mmus = applyL2bDefensiveFilters(ex.mmus);
    // Remembered for "Practice again": clear every streak so the next walk
    // starts at zero (Brief 046 redo semantics). Captured BEFORE the mastery
    // filter so a fully-mastered lesson can still reset its full streak set.
    this._allItemMmuIds = mmus.map((m) => m.mmuId);
    // Brief 020: the same list, inflated, for the reveal — the whole lesson in
    // entry order, taken before the mastery filter for the same reason.
    this._exerciseMmus = mmus;

    // Brief 003a: product-faithful Training pool — present only not-yet-mastered
    // MMUs, in original (pedagogical) order. Filter, never re-sort (Gotcha 1).
    // Mirrors KFE Web's <kfe-l2b-exercise> onConnected pool-filter against the
    // learner's mastered set.
    const masteredIds = new Set(learner.getMasteredMmuIds(DEFAULT_MASTERY_STREAK));
    const pool = mmus.filter((m) => !masteredIds.has(m.mmuId));
    // Brief 020: what this filter removed is what the learner already had. Read
    // it HERE, from the filter that actually ran, rather than re-reading the
    // store later — when storage is blocked (a partitioned third-party iframe
    // is the demo's home) nothing is filtered and this is 0, which is exactly
    // the honest answer in that case.
    this._preMastered = mmus.length - pool.length;

    // Empty-after-filter: every MMU mastered. Don't build an engine on an empty
    // pool (the analogous KFE edge — Gotcha 3).
    if (pool.length === 0) {
      this._allMastered = true;
      // Brief 020c — THE RETURNING VISITOR GETS THE REVEAL, not the 003a
      // completion panel. That panel's Continue goes straight to Kickstarter,
      // which walks anyone who has finished once on this origin (a returning
      // visitor, or anyone who reloads after completing) past the entire payload
      // and the ask — the funnel simply never happens for them. Confirmed
      // on-device 2026-09-10.
      //
      // The reveal needs nothing the run would have supplied: it renders from
      // `_exerciseMmus` (the full six, captured above before the mastery filter)
      // and resolves its own images, so mounting it here is the same pane a
      // fresh finisher sees — and its Continue already advances to the email
      // panel, so they land in the same reveal → Register/Skip flow.
      //
      // `_revealEligible()` keeps this Mandarin-only: in the English direction
      // `_allMastered` still renders today's completion panel and CTA, exactly
      // as before. "Practice again" lives on the reveal's action row, so the
      // re-drill this panel used to offer is still one tap away.
      if (this._revealEligible()) {
        this._revealMounted = true;
        this.classList.add("l2b-reveal");
        // No run happened, so there are no decoded tiles to seed from — the
        // async pass resolves all six and re-renders when they land.
        this._prepareRevealImages();
        // A genuine reveal mount, so it counts as one — deduped by the same flag
        // the post-run mount uses, so a re-render can never double it.
        if (!this._revealTracked) {
          this._revealTracked = true;
          track("reveal_view");
        }
      }
      this._scheduleRender();
      return;
    }

    const itemData = mmusToItemData(pool);
    this.engine = new L2BEngine(itemData, {
      defaultMasteryStreak: DEFAULT_MASTERY_STREAK,
      modality: modalityFor(this._l2bType),
    });

    const onState = () => {
      const snap = this.engine?.snapshot();
      if (!snap) return;
      // Brief 016: the lesson finished. The complete state can be published
      // more than once (a late feedback timer, a re-render) — the flag keeps
      // the count honest; "Practice again" clears it for the next attempt.
      if (snap.mode === "complete" && !this._lessonCompleteTracked) {
        this._lessonCompleteTracked = true;
        track("lesson_complete", { mastered_count: snap.masteredCount, total_items: snap.totalItems });
        // Brief 020: the reveal mounts HERE, on the same once-per-lesson edge
        // and behind the same flag, so a re-published complete state can't
        // re-fire `reveal_view` or restart the image prep. It replaces the
        // Continue CTA in the Mandarin direction only — flipped to "Learn
        // English" the lesson finishes exactly as it does today.
        if (this._revealEligible()) {
          this._revealMounted = true;
          this.classList.add("l2b-reveal");
          // The run just decoded all six tiles, so paint from those and let the
          // async pass below only fill gaps — no blank cells on the first frame.
          this._revealUrls = { ...this._tileUrls };
          this._prepareRevealImages();
          if (!this._revealTracked) {
            this._revealTracked = true;
            track("reveal_view");
          }
        }
      }
      // Brief 134: hold the current frame until the NEXT question's tiles are
      // fully resolved + decoded, then repaint target + grid in lockstep — no
      // blank-tile window that signals the answer. The feedback frame reuses
      // the prior round's already-preloaded tiles, so it renders at once.
      if (snap.mode === "tileGrid" && snap.tiles.length) {
        this._prepareTilesThenRender(snap);
      } else {
        this._scheduleRender();
      }
    };
    const onResult = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      const mmuId = Number(detail.conceptId);
      if (Number.isFinite(mmuId)) learner.recordAttempt(mmuId, !!detail.correct);
      this._trackResult(String(detail.conceptId), !!detail.correct);
    };

    this.engine.addEventListener("stateChanged", onState);
    this.engine.addEventListener("result", onResult);
    // Register every engine listener via addCleanup so re-entering the screen
    // doesn't stack handlers (and audio) — CLAUDE.md event-stacking pitfall.
    this.addCleanup(() => this.engine?.removeEventListener("stateChanged", onState));
    this.addCleanup(() => this.engine?.removeEventListener("result", onResult));

    // Don't auto-start: the first play() must follow a user gesture (Start tap).
    this._scheduleRender();
  }

  onDisconnected() {
    if (this._feedbackTimer) clearTimeout(this._feedbackTimer);
    audio.stop();
  }

  _handleStart() {
    if (!this.engine || this._started) return;
    this._started = true;
    // Brief 020: the abandon clock. Everything `run_abandon` reports about
    // elapsed time is measured from this tap, because before it there is
    // nothing to be slow at — the learner is still on the entry screen.
    this._startedAt = performance.now();
    // Brief 016: the learner left the Start prompt into the lesson. `_started`
    // is the dedupe — it's set above and only cleared by "Practice again", so
    // re-renders can't re-fire this.
    track("lesson_start");
    const state = this.engine.start();
    this._afterStateChange(state);
  }

  /**
   * Brief 016 (GA4): one tile answer. Fires `item_attempt` for every result and
   * `item_mastered` the first time an item's streak reaches the mastery target.
   *
   * The streak is mirrored HERE rather than read off the engine: the engine's
   * `result` event carries only `{ correct, conceptId }`, and its snapshot at
   * this moment is still the pre-result state, so `masteredCount` hasn't moved
   * yet. Mirroring the engine's own rule (correct → +1, wrong → reset to 0,
   * mastered at `DEFAULT_MASTERY_STREAK`) keeps the engine pure (Rule #1).
   *
   * NO PII: the conceptId is a Map key only — nothing about the word, and no
   * identifier, leaves in the event params (house rule / criterion 5).
   *
   * @param {string} conceptId
   * @param {boolean} correct
   */
  _trackResult(conceptId, correct) {
    this._attemptCount++;
    // Brief 020: prompt → first drop. Long, with audio fine, reads as
    // expectation rather than breakage (RULINGS §2); absent means there never
    // was a drop at all, which is a different thing from "it took 0ms".
    if (this._firstAttemptMs === null && this._startedAt > 0) {
      this._firstAttemptMs = Math.round(performance.now() - this._startedAt);
    }
    track("item_attempt", { correct, round: this._attemptCount });

    const streak = correct ? (this._streaks.get(conceptId) ?? 0) + 1 : 0;
    this._streaks.set(conceptId, streak);
    if (streak >= DEFAULT_MASTERY_STREAK && !this._masteredTracked.has(conceptId)) {
      this._masteredTracked.add(conceptId);
      track("item_mastered", { round: this._attemptCount });
    }
  }

  _handleAdvance() {
    if (!this.engine) return;
    const state = this.engine.onAdvancePressed();
    this._afterStateChange(state);
  }

  _handleReplay() {
    if (!this.engine) return;
    const state = this.engine.snapshot();
    const target = state.targetItem ?? state.introItem;
    if (target) this._playAudioForTarget(target, /*force*/ true);
  }

  /** @param {string} conceptId */
  _handlePick(conceptId) {
    if (!this.engine) return;
    if (this._feedbackTimer) return; // ignore double-taps during feedback
    const fb = this.engine.onTileSelected(conceptId);
    this._afterStateChange(fb);
    this._feedbackTimer = setTimeout(() => {
      this._feedbackTimer = null;
      const next = this.engine?.onFeedbackComplete();
      if (next) this._afterStateChange(next);
    }, FEEDBACK_MS);
  }

  /** @param {import("../engines/l2b-engine.js").L2BState} state */
  _afterStateChange(state) {
    const prevMode = this._lastMode;
    this._lastMode = state.mode;

    // Brief 020: mirror the engine's met universe screen-side for
    // `run_abandon`'s `met_count`. An item is met the first time it is put in
    // front of the learner — as a large intro, or as a tile-grid target. The
    // engine knows this and tells nobody (Rule #1), so the screen counts.
    const met = state.introItem?.conceptId ?? state.targetItem?.conceptId;
    if (met) this._metIds.add(String(met));

    const autoPlay = this._renderFlags.autoPlay;
    if (state.mode === "largeIntro" && state.introItem) {
      // Brief 006f: every new intro starts with the translation hidden (ⓘ only).
      this._translationRevealed = false;
      if (autoPlay) this._playAudioForTarget(state.introItem, false);
      this._resolveLargeIntroImage(state.introItem);
    } else if (state.mode === "tileGrid" && state.targetItem) {
      // Replay whenever we ENTER tileGrid from anywhere else; the conceptId
      // guard inside _playAudioForTarget covers back-to-back same-target frames.
      if (autoPlay) {
        const force = prevMode !== "tileGrid";
        this._playAudioForTarget(state.targetItem, force);
      }
    }
    // No scheduleRender — the stateChanged listener already did.
  }

  /**
   * Brief 134: pre-resolve + decode every tile image for `state` BEFORE swapping
   * the visible grid, so the target prompt and all tiles repaint together. While
   * this awaits, the screen keeps showing the previous frame (the feedback ✓/✗
   * over the old grid) — that hold IS the atomic swap. The `_prepareToken` guard
   * drops the result if a newer state superseded this one mid-resolve.
   * @param {import("../engines/l2b-engine.js").L2BState} state
   */
  async _prepareTilesThenRender(state) {
    const tiles = state.tiles ?? [];
    const token = ++this._prepareToken;
    /** @type {Record<number,string>} */
    const urls = {};
    await Promise.all(
      tiles.map(async (d) => {
        const url = await resolveImage(d.mmu);
        if (!url) return;
        urls[d.mmu.mmuId] = url;
        await preloadImage(url);
      }),
    );
    // A newer transition (fast tap, redo, unmount) won the race — discard.
    if (token !== this._prepareToken || !this._connected) return;
    this._tileUrls = urls;
    this._scheduleRender();
  }

  /**
   * @param {import("../engines/l2b-engine.js").L2BItemData} target
   * @param {boolean} force
   */
  _playAudioForTarget(target, force) {
    if (!force && this._lastAudioConceptId === target.conceptId) return;
    this._lastAudioConceptId = target.conceptId;
    // Brief 020: the next `playing` event on the shared element is a PROMPT
    // starting, so `audio_ok` may be set from it (see `_installAudioProbe`).
    this._promptAudioPending = true;
    // Target (L2) phrase audio — the English `.ogg` set, routed via the language
    // manager (Brief 004) rather than the default resolveAudio.
    audio.play({ url: lang.targetAudioUrl(target.mmu) }).catch((e) => {
      console.warn("[l2b] audio play failed for", target.conceptId, e);
    });
  }

  /**
   * Play the L1 scaffolding audio (the mother-tongue rendering) for the
   * large-intro translation affordance. Derived from the phrase, so the file is
   * `${prefix}${PhraseNoSpaces}.ogg` (e.g. `zh_…`).
   * @param {import("../engines/l2b-engine.js").L2BItemData} target
   */
  _playL1Audio(target) {
    // Brief 020: the ⓘ affordance is not the prompt. A learner who only ever
    // heard the translation still never heard the thing they were asked to
    // drag, and `audio_ok` must not claim otherwise.
    this._promptAudioPending = false;
    audio.play({ url: lang.l1AudioUrl(target.mmu) }).catch((e) => {
      console.warn("[l2b] L1 audio play failed for", target.conceptId, e);
    });
  }

  /**
   * Brief 020 — `audio_ok`. Listens for the NATIVE `playing` event on the shared
   * audio element, which fires only when playback has actually begun. That is
   * the whole point: iOS Safari will not play audio without a user gesture and a
   * cross-origin iframe adds restrictions on top, so if the Start unlock ever
   * fails every prompt is silent, the learner cannot know what to drag, and it
   * presents in the data as ordinary early disinterest. `AudioEngine`'s own
   * `"playing"` event is dispatched BEFORE `el.play()` is called, so it would
   * report intent rather than sound — this listens to the element, not the
   * engine. Guarded + `addCleanup`d so a remount never stacks handlers.
   */
  _installAudioProbe() {
    if (this._audioOff) return;
    const onPlaying = () => {
      if (this._promptAudioPending) this._audioOk = true;
    };
    audio.el.addEventListener("playing", onPlaying);
    this._audioOff = () => audio.el.removeEventListener("playing", onPlaying);
    this.addCleanup(() => { this._audioOff?.(); this._audioOff = null; });
  }

  /**
   * Brief 020 — the two ways a page goes away. BOTH are needed: iOS Safari
   * frequently gives only `pagehide`, desktop tab-switching only
   * `visibilitychange`. Guarded + `addCleanup`d so the direction toggle's
   * remount never stacks handlers.
   *
   * Brief 022 — and the third thing that happens, which 020 had no answer for:
   * they come BACK. `visibilitychange`→visible fires `run_resume`, which is what
   * turns the analysis from clever into trivial — an abandon whose `hide_seq` a
   * resume matches was a distraction, not a departure. No session-stitching, no
   * join against `lesson_complete`.
   *
   * Do NOT drop `visibilitychange` in favour of `pagehide` alone. It is the last
   * reliable signal on mobile — a phone browser swiped away or killed by the OS
   * frequently never fires `pagehide` at all — and losing it would trade an
   * inflated count for a systematically missing one, missing exactly the mobile
   * abandons that matter most.
   */
  _installAbandonListeners() {
    if (this._abandonOff) return;
    const onPageHide = () => this._fireAbandon();
    const onVisibility = () => {
      if (document.visibilityState === "hidden") this._fireAbandon();
      else this._fireResume();
    };
    // Brief 022a — bfcache insurance. Entering the back/forward cache fires
    // `pagehide` (persisted) and latches the hide, which is right: they left.
    // Coming BACK fires `pageshow`, and `visibilitychange` is *usually* fired
    // alongside it — but that is not guaranteed across browsers, and if it is
    // missed the latch never releases and NO further `run_abandon` is emitted
    // for the rest of the page load. Silent, total, and invisible in the data.
    // `_fireResume` is idempotent per hidden period, so the belt and the braces
    // cannot double-count each other. Matters most on mobile Safari, which
    // leans on bfcache for back-navigation — the traffic this instrument is
    // mostly measuring.
    const onPageShow = () => this._fireResume();
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibility);
    this._abandonOff = () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    this.addCleanup(() => { this._abandonOff?.(); this._abandonOff = null; });
  }

  /**
   * Brief 020 — `run_abandon`. The only instrument that tells an early leave
   * from a late one, and nothing downstream can reconstruct it afterwards.
   *
   * Sent by BEACON (`trackBeacon`): a normal gtag call on unload is dropped
   * often enough to bias the dataset toward the people who stayed — precisely
   * the population this event exists to measure around.
   *
   * FIRED AT THE REVEAL TOO. Leaving after the payload is not a failure — that
   * is the fork working — but it has to be distinguishable from leaving
   * mid-drill, and `stage` is what distinguishes them. So completion does not
   * suppress the event.
   *
   * THE ACCEPTANCE TEST (RULINGS §3): conditional completion — of learners with
   * `attempts > 3`, the fraction reaching `lesson_complete` — must be derivable
   * from this event plus `lesson_complete`, with nothing else added.
   *
   * Brief 022 — FIRES ON EVERY HIDE, numbered by `hide_seq`. 020 sent one event
   * per page load and froze its payload at the first hide; a learner who looked
   * at another tab at attempt 10 and went on to finish at 24 was recorded as an
   * abandonment at 10. That inflates the count AND makes the histogram cluster
   * in the middle by construction, because a distraction is likeliest in the
   * body of a run — a measurement that can only return one answer. Now the row
   * to read is the highest `hide_seq` with no matching `run_resume`: that one
   * carries the true final state, and the ones a resume matches were glances.
   *
   * Deduplicated across the whole hidden period, so a real close — which fires
   * `visibilitychange`→hidden AND `pagehide` — is one row, not two.
   *
   * NO PII: enums, bools and counts. Nothing about the words.
   */
  _fireAbandon() {
    if (_hideLatched) return;
    _hideLatched = true;
    _hideSeq += 1;
    const now = performance.now();
    // Brief 024 — the start of this hidden period. Reuses `now` rather than
    // calling the clock again so `hidden_ms` and `elapsed_ms` share a basis.
    _hiddenAt = now;

    const started = this._startedAt > 0;
    // `entry` = Start not yet taken, or taken with zero attempts. `reveal` = the
    // reveal pane had mounted. Everything between is `run`.
    const stage = this._revealMounted
      ? "reveal"
      : started && this._attemptCount > 0
        ? "run"
        : "entry";

    /** @type {Record<string, unknown>} */
    const params = {
      stage,
      // Brief 022: which hide this is, counted from the start of the PAGE LOAD.
      // Needs a GA4 custom dimension (`hide_seq`, event scope) or it is
      // invisible, and registration is not retroactive.
      hide_seq: _hideSeq,
      attempts: this._attemptCount,
      met_count: this._metCount(),
      mastered_count: this._masteredCount(),
      elapsed_ms: started ? Math.round(now - this._startedAt) : 0,
      audio_ok: this._audioOk,
    };
    // ABSENT, not zero, when there never was a drop — "they never answered" and
    // "they answered instantly" are opposite findings and must not collapse.
    if (this._firstAttemptMs !== null) params.first_attempt_ms = this._firstAttemptMs;

    trackBeacon("run_abandon", params);
    // Prioritise this session for recording so the abandons — the ones actually
    // worth watching — aren't the ones Clarity samples away (RULINGS §2). One
    // line, once Clarity is installed INSIDE the demo app; that is its own
    // decision and is out of scope here (Brief 020 step 4).
    // clarity("upgrade", "abandon");
  }

  /**
   * Brief 022 — `run_resume`. The learner came back.
   *
   * This is the half that makes the abandonment data readable: a `run_abandon`
   * whose `hide_seq` is matched by a `run_resume` in the same session was not an
   * abandonment, it was a glance at another tab. Nothing needs to be joined
   * against `lesson_complete` and no sessions need stitching — the pair is the
   * proof, and its absence is the proof of the other thing.
   *
   * It is also a finding in its own right. How many people come back after
   * looking away is the first hard evidence for or against the flow-state claim
   * (RULINGS §3), and nothing else in the instrument can say.
   *
   * NOT a beacon: the page is alive and normal transport is correct and cheaper.
   * `trackBeacon` exists because an unload hit is dropped often enough to bias
   * the dataset; none of that applies here.
   *
   * Also where the hide latch is RELEASED, and the only place it is: becoming
   * visible again is the only way to depart a second time, so releasing here is
   * what makes the next departure countable while a close still costs one row.
   *
   * Brief 022a — the latch is now the GUARD as well as the thing released, so
   * this is idempotent per hidden period exactly as `_fireAbandon` is. Three
   * reasons it has to be:
   *
   *   1. Two screen instances can coexist — the host re-creates this element on
   *      render (019a) — and both hold listeners. Unguarded, the first call
   *      released the latch and the second still emitted, putting two
   *      `run_resume` rows at the same `hide_seq`. That does not break the
   *      matching (which only asks whether a match EXISTS) but it inflates the
   *      resume count, and how many people come back is a finding in its own
   *      right, not just a correction term.
   *   2. `pageshow` and `visibilitychange`→visible both fire on a bfcache
   *      restore. Belt and braces must not double-count.
   *   3. It subsumes the old `_hideSeq > 0` check: with no hide there is no
   *      latch, so a screen mounting into an already-hidden page — a prerender,
   *      a background tab — still cannot report a resume of nothing.
   *
   * Brief 024 — it now carries HOW LONG they were away, as `hidden_ms`. The
   * duration is not knowable at hide time, so there is no version of it that
   * lives on the `run_abandon` row; and it is measured in the page off a
   * monotonic clock rather than derived in BigQuery from the two event
   * timestamps, because one of those operands is a `sendBeacon` delivery time
   * on the unload path — the least trustworthy clock in the instrument, and
   * worst on mobile, which is the platform where a long hide is most
   * interesting. `_hiddenAt` is deliberately NOT cleared here: the latch
   * already guarantees one resume per hidden period and the next
   * `_fireAbandon` overwrites it, so clearing would add a second piece of
   * state to keep consistent with the latch and buy nothing.
   */
  _fireResume() {
    if (!_hideLatched) return;
    _hideLatched = false;
    track("run_resume", {
      hide_seq: _hideSeq,
      attempts: this._attemptCount,
      // ABSENT, not zero, if there was somehow no hide behind this — same house
      // rule as `first_attempt_ms`: a `hidden_ms: 0` and a missing `hidden_ms`
      // must not be the same row. The guard is provably unreachable (no resume
      // without the latch, no latch without `_fireAbandon`, and `_fireAbandon`
      // always stamps `_hiddenAt`) and is written anyway, because it costs
      // nothing and an unguarded `0` down an unforeseen path is a silent data
      // error.
      ...(_hiddenAt ? { hidden_ms: Math.round(performance.now() - _hiddenAt) } : {}),
    });
  }

  /**
   * MMUs introduced so far. Both counts are out of the six a stranger meets —
   * the run-scoped mirror plus whatever 003a filtered out as already mastered
   * before it — and are clamped so a returning learner can never report 7 of 6.
   * One method each so `run_abandon` and the signup POST (Brief 026) report the
   * SAME number under the same name.
   * @returns {number}
   */
  _metCount() {
    return Math.min(this._allItemMmuIds.length, this._preMastered + this._metIds.size);
  }

  /** MMUs mastered so far — see `_metCount`. @returns {number} */
  _masteredCount() {
    return Math.min(this._allItemMmuIds.length, this._preMastered + this._masteredTracked.size);
  }

  /**
   * Brief 020 — is this run entitled to the reveal? Mandarin direction only:
   * flipped to "Learn English" the lesson runs and finishes exactly as it does
   * today, on the Continue CTA, with no reveal and no second set of copy
   * (criterion 8). Also needs both halves of the payload to actually exist —
   * a lesson with no recombination in it has nothing to reveal, so it falls
   * back rather than rendering a half-empty pane.
   * @returns {boolean}
   */
  _revealEligible() {
    try {
      if (lang.current().targetLanguage !== "zh") return false;
    } catch {
      return false; // language manager not hydrated — no reveal, no throw
    }
    const { atoms, sentences } = this._revealSets();
    return atoms.length > 0 && sentences.length > 0;
  }

  /**
   * Brief 020 — the reveal's two groups, in reveal order.
   *
   * Grouping is DATA-DRIVEN: `isLargeImage` is how the corpus already
   * distinguishes an atom (走路, 门, 站起来, 窗户) from a recombination
   * (走到门前, 走到窗户前), so the curation follows the content rather than a
   * second list hardcoded here. Ordering WITHIN each group comes from
   * `REVEAL_ORDER` (the v3 design's order); anything the run carries that
   * `REVEAL_ORDER` doesn't name still appears, after the named ones, in entry
   * order — so restoring the full thirteen degrades into something sensible
   * rather than dropping items on the floor.
   * @returns {{ atoms: any[], sentences: any[] }}
   */
  _revealSets() {
    const byId = new Map(this._exerciseMmus.map((m) => [m.mmuId, m]));
    const ordered = [];
    for (const id of REVEAL_ORDER) {
      const m = byId.get(id);
      if (m) { ordered.push(m); byId.delete(id); }
    }
    for (const m of byId.values()) ordered.push(m);
    return {
      atoms: ordered.filter((m) => m.isLargeImage),
      sentences: ordered.filter((m) => !m.isLargeImage),
    };
  }

  /**
   * Brief 020 — resolve + decode every reveal drawing. Seeded from the run's own
   * `_tileUrls` at mount (the six were decoded rounds ago, so the pane paints
   * with drawings immediately); this pass exists for the gaps — a lesson bigger
   * than the grid, or an image that never made it into a tile. Token-guarded the
   * same way `_prepareTilesThenRender` is.
   */
  async _prepareRevealImages() {
    const token = ++this._revealUrlsToken;
    /** @type {Record<number,string>} */
    const urls = { ...this._revealUrls };
    await Promise.all(
      this._exerciseMmus.map(async (m) => {
        if (urls[m.mmuId]) return;
        const url = await resolveImage(m);
        if (!url) return;
        urls[m.mmuId] = url;
        await preloadImage(url);
      }),
    );
    if (token !== this._revealUrlsToken || !this._connected) return;
    this._revealUrls = urls;
    this._scheduleRender();
    // Brief 021: the card is built from these same URLs, so its preparation
    // starts HERE rather than at either mount site. The already-complete path
    // (020c's returning visitor) mounts the reveal with `_tileUrls` empty — no
    // run happened — so a prepare fired at mount would build a card with six
    // blank cells. This is the one point both paths pass through with the URLs
    // resolved.
    this._prepareCard();
  }

  /**
   * One reveal row: the Mandarin, and the drawing it was attached to.
   *
   * `alt=""` is deliberate. The drawing is decorative BESIDE the phrase it sits
   * next to, and the only alt text available is the English translation — which
   * is the one thing the reveal exists to have withheld. Leaking it to a screen
   * reader would undo the payload.
   * @param {any} mmu
   * @returns {HTMLElement}
   */
  _revealRow(mmu) {
    const zh = document.createElement("span");
    zh.className = "zh";
    zh.textContent = lang.targetText(mmu);

    const cell = document.createElement("span");
    cell.className = "im";
    const url = this._revealUrls[mmu.mmuId];
    if (url) {
      const img = document.createElement("img");
      img.src = url;
      img.alt = "";
      img.draggable = false;
      cell.appendChild(img);
    }

    const row = document.createElement("div");
    row.className = "r";
    row.append(zh, cell);
    return row;
  }

  /**
   * Brief 020 — the reveal, in place of the Continue CTA. Built to
   * `SHARE-MODAL-l2b-v3.html`, in this order down the pane:
   *
   *   1. the first sentence as hero, with the tile image the learner has been
   *      dragging beside it — visual, not a wall of text
   *   2. "Nobody translated that for you."
   *   3. "You understood" — the atoms, a hairline, then the two sentences
   *   4. "You have never studied Mandarin Chinese."
   *
   * The claim is "nobody translated that", NOT "you had never seen this": by
   * reveal time the learner has met 走到门前 three times in the drill, so
   * *never seen* is false at that moment and *nobody translated it* is not.
   *
   * It ends on a Continue path. v3's own ending — "Would you like a record of
   * what you just understood?" — belongs to brief 021, because the record does
   * not exist yet and asking with nothing behind it is worse than not asking;
   * the email panel that 020 planned to put here is deferred to 020b (its
   * endpoint contract is not pinned). Both insert ABOVE this Continue. Nothing
   * here is stubbed: no disabled button, no placeholder, and the word "card"
   * appears in no string a learner can see.
   * @returns {DocumentFragment}
   */
  _renderReveal() {
    const { atoms, sentences } = this._revealSets();
    const hero = sentences[0];

    const heroImage = document.createElement("div");
    heroImage.className = "hero-im";
    const heroUrl = this._revealUrls[hero.mmuId];
    if (heroUrl) {
      const img = document.createElement("img");
      img.src = heroUrl;
      img.alt = "";
      img.draggable = false;
      heroImage.appendChild(img);
    }

    const atomRows = atoms.map((m) => this._revealRow(m));
    const madeRows = sentences.map((m) => this._revealRow(m));

    const again = document.createElement("button");
    again.type = "button";
    again.className = "ghost";
    again.textContent = t("l2b.practiceAgain");
    again.addEventListener("click", () => this._onPractiseAgain());

    // Brief 020b — Continue now ADVANCES rather than exits. 020 shipped this as
    // an <a target="_top"> straight to the Kickstarter because the reveal was
    // the last screen the demo had; 020b puts the ask in between, so the same
    // button (same label, same primary weight, same place) opens the email
    // panel and the outbound links move onto the panel's own endings, where
    // they are still one tap away.
    //
    // Reaching this method already means the reveal is entitled to run
    // (`_revealEligible()` gates `_revealMounted`, and that is Mandarin-only),
    // so the English direction never sees this button — it finishes on
    // `_renderCompletion` and today's `_continueCta` exactly as before.
    const advance = document.createElement("button");
    advance.type = "button";
    advance.className = "cta continue";
    advance.textContent = t("nav.continue");
    advance.addEventListener("click", () => this._showEmailPanel());

    // Brief 021 — Share, between Continue and Practice again. "Beside
    // Continue", as the brief has it: the payload is what a convinced visitor
    // wants to pass on, and the moment to offer that is while they are still
    // looking at it — not after the email ask, and not on the endings.
    const share = this._shareControl();

    return html`
      <section class="reveal">
        <div class="reveal-cols">
          <div class="hero-wrap">
            <div class="hero-row">
              <div class="hero-zh">${lang.targetText(hero)}</div>
              ${heroImage}
            </div>
            <p class="hero-claim">${t("l2b.reveal.claim")}</p>
          </div>
          <div class="built">
            <div class="built-label">${t("l2b.reveal.understood")}</div>
            <div class="scrollpart">
              <div class="rowset">${atomRows}</div>
              <div class="rowset made">${madeRows}</div>
            </div>
          </div>
        </div>
        <div class="tail">
          <p class="line">${t("l2b.reveal.line")}</p>
          <div class="actions">
            ${advance}
            ${share}
            ${again}
          </div>
        </div>
      </section>
    `;
  }

  // ─── Brief 021: the shareable record card ─────────────────────────────────
  //
  // The reveal is the double-take; this is what lets a convinced visitor hand
  // it to somebody else. No Worker and no server: the card is FIXED — the same
  // six for everyone — so it renders client-side from the drawings the reveal
  // has already resolved and goes out through the Web Share API, with a
  // download as the desktop fallback. See `app/ui/record-card.js` for the SVG
  // and the canvas, including why the two are composited rather than nested.

  /**
   * The card's content, from the reveal's own two groups.
   *
   * SLICED TO THE FIXED COMPOSITION — two sentences, four atoms. `_revealSets()`
   * degrades gracefully if the parked thirteen are ever restored (extra items
   * appear after the named ones), and the REVEAL should: it is a scrolling list.
   * The card is not. It is one composed image with four columns of a known
   * width, and letting eleven atoms into it would produce an unreadable card
   * rather than a longer one.
   * @returns {import("../ui/record-card.js").CardModel}
   */
  _cardModel() {
    const { atoms, sentences } = this._revealSets();
    /** @param {any} m */
    const item = (m) => ({ zh: lang.targetText(m), url: this._revealUrls[m.mmuId] });
    return {
      sentences: sentences.slice(0, 2).map(item),
      atoms: atoms.slice(0, 4).map(item),
      builtFrom: t("l2b.card.builtFrom"),
      // Two lines, not one wrapped sentence: the break is where the claim turns
      // from what they did into what they never did, and that is the beat the
      // whole card is built on. No count and no time claim anywhere — v3's "two
      // minutes" watermark is dropped (policy).
      lines: [t("l2b.card.line1"), t("l2b.card.line2")],
      markLead: t("l2b.card.markLead"),
      markUrl: t("l2b.card.markUrl"),
    };
  }

  /**
   * Start building the card, once, as the reveal paints.
   *
   * All of it — the six CORS image loads, the composite and the PNG encode —
   * happens off the gesture, while the learner is reading. That is the iOS Web
   * Share requirement taken as far as it goes (Brief 021 Gotcha 3): not "keep
   * the async gap tight" but leave no gap at all.
   *
   * Best-effort by construction. A rejection is swallowed here and surfaces
   * later as a failed generate, which is the only place it can be acted on.
   */
  _prepareCard() {
    if (this._cardPrep || this._cardBlob) return;
    this._cardPrep = prepareCard(this._cardModel());
    this._cardPrep.catch(() => { /* handled in `_generateCard` */ });
  }

  /**
   * Take delivery of the PNG — WITHOUT reporting anything.
   *
   * Split from the record event on purpose (Brief 021a, the chooser). Opening
   * the share chooser warms the blob so that whichever option the learner then
   * taps — Share or Download — has bytes in hand and stays inside its own
   * gesture; but merely opening the chooser, or copying the link, is not a
   * record taken. `record_generate` therefore lives in `_markRecord`, fired
   * only by an image action, and this method carries no event at all.
   * @returns {Promise<Blob | null>}  `null` when the card could not be built
   */
  async _ensureBlob() {
    if (this._cardBlob) return this._cardBlob;
    try {
      if (!this._cardPrep) this._prepareCard();
      const blob = await this._cardPrep;
      if (!blob) return null;
      this._cardBlob = blob;
      return blob;
    } catch {
      // No canvas, no 2d context, a rasteriser that refused the SVG, a tainted
      // surface. Nothing here is worth throwing out of a click handler; the
      // caller falls back or disables the control instead.
      return null;
    }
  }

  /**
   * Report the record, once per page load. Fired by an IMAGE action (Share or
   * Download), not by opening the chooser and not by Copy link — so the count
   * stays what the funnel needs it to mean: one learner took one record. This
   * is where SHARE-MODAL-l2b-v3.html's own prototype fires it.
   */
  _markRecord() {
    if (this._cardTracked) return;
    this._cardTracked = true;
    track("record_generate");
  }

  /**
   * Can this browser share FILES? Not "does it have a share sheet" — Chrome on
   * the desktop has `navigator.share` and refuses files, and sharing the caption
   * without the card would be sharing nothing.
   *
   * Probed with an empty PNG because `canShare` answers on the file's TYPE, not
   * its bytes, and this runs at render time when no blob exists yet. It decides
   * the control's LABEL only; the click path re-checks with the real file.
   * @returns {boolean}
   */
  _canShareFiles() {
    try {
      const nav = /** @type {any} */ (navigator);
      if (typeof nav.share !== "function" || typeof nav.canShare !== "function") return false;
      const probe = new File([new Uint8Array(0)], CARD_FILENAME, { type: "image/png" });
      return nav.canShare({ files: [probe] });
    } catch {
      return false; // no File constructor, no navigator — no share
    }
  }

  /**
   * The Share control: a ghost pill on the reveal's pinned row.
   *
   * It always says "Share" and always does ONE thing — open the chooser
   * (Brief 021a). The single auto-detecting button it replaced trusted
   * `navigator.canShare({files})`, which desktop Chrome answers `true` while its
   * `share()` then opens nothing and never rejects — a dead button with no
   * fallback. The chooser makes the platform's options explicit (Share…,
   * Download image, Copy link) and, as a bonus, each option fires from its OWN
   * tap, so the iOS transient-activation rule is satisfied by construction.
   * @returns {HTMLButtonElement}
   */
  _shareControl() {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "ghost share";
    btn.textContent = t("l2b.share.button");
    // The visible word is compact so three controls fit the 013a budget; the
    // accessible name is the whole affordance.
    btn.setAttribute("aria-label", t("l2b.share.label"));
    btn.title = t("l2b.share.label");
    btn.setAttribute("aria-haspopup", "menu");
    btn.setAttribute("aria-expanded", "false");
    btn.addEventListener("click", () => this._openShareMenu(btn));
    return btn;
  }

  /**
   * Open the share chooser.
   *
   * Warms the blob on open (`_ensureBlob`, no event) so that whichever option
   * the learner then taps has bytes in hand and stays synchronous inside its own
   * gesture — the encode has been running since the reveal mounted, so this is
   * normally already settled. Opening the chooser reports nothing; the record
   * event belongs to an image action (`_markRecord`).
   * @param {HTMLElement} trigger  the reveal button, for focus return
   */
  _openShareMenu(trigger) {
    if (this._shareMenuEl) return;
    this._ensureBlob(); // fire-and-forget: populates _cardBlob if it can

    const overlay = document.createElement("div");
    overlay.className = "share-overlay";
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this._closeShareMenu(); // backdrop only
    });

    const menu = document.createElement("div");
    menu.className = "share-menu";
    menu.setAttribute("role", "menu");
    menu.setAttribute("aria-label", t("l2b.share.label"));

    const close = document.createElement("button");
    close.type = "button";
    close.className = "share-menu-close";
    close.setAttribute("aria-label", t("l2b.share.menu.close"));
    close.textContent = "×"; // ×
    close.addEventListener("click", () => this._closeShareMenu());

    const title = document.createElement("p");
    title.className = "share-menu-title";
    title.textContent = t("l2b.share.menu.title");

    menu.append(close, title);

    /** @param {string} label @param {() => void} act */
    const item = (label, act) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "share-menu-item";
      b.setAttribute("role", "menuitem");
      b.textContent = label;
      b.addEventListener("click", act);
      menu.append(b);
      return b;
    };

    // Share… only where the browser can actually share files (iOS/Android).
    // Desktop, which cannot, simply does not see it — no dead option.
    if (this._canShareFiles()) item(t("l2b.share.menu.native"), () => this._chooseShare());
    item(t("l2b.share.download"), () => this._chooseDownload());
    const copyBtn = item(t("l2b.share.menu.copy"), () => this._copyLink(copyBtn));

    overlay.append(menu);
    this.shadowRoot?.append(overlay);
    this._shareMenuEl = overlay;
    this._shareMenuTrigger = trigger;
    trigger.setAttribute("aria-expanded", "true");

    // Esc closes; Tab is trapped inside the menu so focus cannot wander back to
    // the reveal underneath. Removed on close so it never outlives the popover.
    /** @param {KeyboardEvent} e */
    this._shareMenuKeydown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); this._closeShareMenu(); return; }
      if (e.key !== "Tab") return;
      const focusables = [...menu.querySelectorAll("button")];
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = this.shadowRoot?.activeElement;
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    this.shadowRoot?.addEventListener("keydown", /** @type {any} */ (this._shareMenuKeydown));

    // Focus the first ACTION so a keyboard user lands on something they can do,
    // not the close affordance.
    /** @type {HTMLButtonElement | null} */
    const firstItem = menu.querySelector(".share-menu-item");
    (firstItem ?? close).focus();
  }

  /** Close the chooser, detach its key handler, and hand focus back. */
  _closeShareMenu() {
    if (this._shareMenuKeydown) {
      this.shadowRoot?.removeEventListener("keydown", /** @type {any} */ (this._shareMenuKeydown));
      this._shareMenuKeydown = null;
    }
    this._shareMenuEl?.remove();
    this._shareMenuEl = null;
    const trigger = this._shareMenuTrigger;
    this._shareMenuTrigger = null;
    if (trigger) {
      trigger.setAttribute("aria-expanded", "false");
      trigger.focus();
    }
  }

  /**
   * "Share…" — the native sheet, from this option's own gesture. `_markRecord`
   * first (an image record is being taken), then hand the cached blob straight
   * to `_shareBlob`; if it is somehow not ready yet, wait for it rather than
   * share nothing (this option only exists where files can be shared).
   */
  _chooseShare() {
    this._closeShareMenu();
    if (this._cardBlob) { this._markRecord(); this._shareBlob(this._cardBlob); return; }
    this._ensureBlob().then((blob) => {
      if (blob) { this._markRecord(); this._shareBlob(blob); }
    });
  }

  /** "Download image" — save the PNG, from this option's own gesture. */
  _chooseDownload() {
    this._closeShareMenu();
    if (this._cardBlob) { this._markRecord(); this._downloadCard(this._cardBlob); return; }
    this._ensureBlob().then((blob) => {
      if (blob) { this._markRecord(); this._downloadCard(blob); }
    });
  }

  /**
   * "Copy link" — put the demo entry URL on the clipboard.
   *
   * No blob and no `record_generate`: copying a link is not taking a record. The
   * boxed iframe carries `clipboard-write`, so `navigator.clipboard` works
   * cross-origin; a legacy `execCommand` path covers the rest. `share_intent`
   * with method `copy_link` on success, and the button confirms in place.
   * @param {HTMLButtonElement} btn
   */
  _copyLink(btn) {
    const done = () => {
      track("share_intent", { method: "copy_link" });
      btn.textContent = t("l2b.share.menu.copied");
      btn.disabled = true;
    };
    try {
      const nav = /** @type {any} */ (navigator);
      const p = nav.clipboard?.writeText?.(SHARE_URL);
      if (p && typeof p.then === "function") {
        p.then(done).catch(() => { if (this._copyFallback()) done(); });
      } else if (this._copyFallback()) {
        done();
      }
    } catch {
      if (this._copyFallback()) done();
    }
  }

  /**
   * Legacy clipboard write for browsers without `navigator.clipboard` (or where
   * it rejects). A hidden textarea, select, `execCommand("copy")`. Returns
   * whether the copy reported success.
   * @returns {boolean}
   */
  _copyFallback() {
    try {
      const ta = document.createElement("textarea");
      ta.value = SHARE_URL;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      (this.shadowRoot ?? document.body).append(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }

  /**
   * Hand the card to the share sheet, or to a download.
   *
   * `share_intent` fires on the INTENT, before the sheet resolves — a learner
   * who opens the sheet and changes their mind still chose to share, and
   * `AbortError` is that choice, not a failure. A rejection that is NOT an
   * abort means the share was refused outright; the likeliest cause is the boxed
   * iframe missing `allow="web-share"` (Brief 021 Step 5, a WordPress task), and
   * the honest recovery is to give them the file anyway rather than have the
   * button do nothing at all. That second path fires its own `share_intent`,
   * because a download genuinely happened.
   * @param {Blob} blob
   */
  _shareBlob(blob) {
    const nav = /** @type {any} */ (navigator);
    let file = null;
    try {
      file = new File([blob], CARD_FILENAME, { type: "image/png" });
    } catch { /* no File constructor — fall through to the download */ }

    if (file && typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
      track("share_intent", { method: "web_share" });
      try {
        Promise.resolve(nav.share({ files: [file], text: t("l2b.share.text"), url: SHARE_URL }))
          .catch((/** @type {any} */ err) => {
            if (err && err.name === "AbortError") return;
            this._downloadCard(blob);
          });
      } catch {
        this._downloadCard(blob);
      }
      return;
    }
    this._downloadCard(blob);
  }

  /**
   * The fallback: save the PNG. An `<a download>` on a blob URL, clicked from
   * inside the same gesture.
   *
   * The anchor is created and discarded rather than rendered, because at render
   * time there is no blob to point it at — the control has to be one button
   * whose behaviour is decided when it is pressed.
   * @param {Blob} blob
   */
  _downloadCard(blob) {
    track("share_intent", { method: "download" });
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = CARD_FILENAME;
      a.rel = "noopener";
      // Some engines ignore a click on a node that is not in the document.
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoked late: revoking on the next tick races the browser's own read of
      // the blob and produces a zero-byte file on WebKit.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { /* no blob URLs, no document — nothing further to try */ }
  }

  /**
   * Brief 020b — the email panel: the conversion step, and the only place the
   * demo asks a stranger for anything.
   *
   * IT IS A PANE, NOT A STRIP. `p-email` / `p-done` / `p-out` in
   * SHARE-MODAL-l2b-v3.html are full panes that replace the reveal, which is
   * also why all three states carry "See what you understood" — the payload
   * stays one tap away from every screen that follows it, for the people who
   * registered and the people who did not. It renders inside `.reveal`'s own
   * box so the pinning contract (criterion 4: the body scrolls, the action row
   * does not) is literally the same CSS, not a second copy of it.
   *
   * THE TWO ENDINGS ARE NOT ONE. Registering and skipping cannot land on the
   * same screen: "check your inbox" told to someone who just declined is a lie,
   * and an earlier prototype told it. So `skipped` is its own state with its
   * own copy, leading with what they keep, and the word "inbox" appears in no
   * string it renders.
   *
   * @returns {DocumentFragment}
   */
  _renderEmailPanel() {
    const back = document.createElement("button");
    back.type = "button";
    back.className = "email-back";
    back.textContent = t("l2b.email.back");
    back.addEventListener("click", () => this._showReveal());

    const body = document.createElement("div");
    body.className = "email-body";
    const actions = document.createElement("div");
    actions.className = "actions";

    if (this._emailState === "registered" || this._emailState === "already") {
      // Brief 020c: both endings say "a link is in your inbox", because in both
      // cases one is — `registered` because the endpoint just sent it,
      // `already` (429 `sg_waitlist_email_throttle`) because it sent one
      // earlier this hour and will not send a fourth. Same pane, different
      // sentences: the second names spam, since an unseen link is the likeliest
      // reason they are here submitting again.
      const done = this._emailState === "already" ? "l2b.email.already" : "l2b.email.done";
      // The address is its own string so it can be set apart visually (v3's
      // `.addr`) without the sentence being rewritten around markup.
      const addr = document.createElement("span");
      addr.className = "email-addr";
      addr.textContent = t("l2b.email.done.address");
      const title = document.createElement("p");
      title.className = "email-big";
      title.append(`${t(`${done}.title`)} `, addr);
      const sub = document.createElement("p");
      sub.className = "email-sub";
      sub.textContent = t(`${done}.body`);
      body.append(title, sub);
      // Brief 020c: the full demo is where the magic link lands, so the pane
      // names it — the copy correction that matters most (registration opens
      // lesson 2 of the FULL demo, a different app; it changes nothing about
      // the lesson they are standing in). A quiet link, not a filled CTA: the
      // next move is in the inbox and nothing here should outweigh it.
      const link = this._exitLink(FULL_DEMO_URL, t("l2b.email.done.link"), "email-link");
      body.append(link);
    } else if (this._emailState === "skipped") {
      const title = document.createElement("p");
      title.className = "email-big";
      title.textContent = t("l2b.email.skipped.title");
      const sub = document.createElement("p");
      sub.className = "email-sub";
      sub.textContent = t("l2b.email.skipped.body");
      body.append(title, sub);
      actions.append(
        this._exitLink(FULL_DEMO_URL, t("l2b.email.skipped.fullDemo"), "cta continue"),
        this._exitLink(HOW_IT_WORKS_URL, t("l2b.email.skipped.how"), "ghost"),
      );
    } else {
      const title = document.createElement("p");
      title.className = "email-big";
      title.textContent = t("l2b.email.prompt.title");
      const sub = document.createElement("p");
      sub.className = "email-sub";
      sub.textContent = t("l2b.email.prompt.body");

      const field = document.createElement("input");
      field.type = "email";
      field.className = "email-field";
      field.placeholder = t("l2b.email.placeholder");
      field.setAttribute("aria-label", t("l2b.email.inputLabel"));
      field.autocomplete = "email";
      field.value = this._emailValue;
      // Mirror every keystroke onto the instance: a re-render (an i18n event, a
      // resize-driven repaint) rebuilds this input from scratch, and without
      // this the learner's half-typed address would vanish under them.
      field.addEventListener("input", () => { this._emailValue = field.value; });
      // Enter submits, because a one-field form where Return does nothing feels
      // broken — and on a phone keyboard "Go" is the obvious next tap.
      field.addEventListener("keydown", (e) => {
        if (e.key === "Enter") { e.preventDefault(); this._onRegister(); }
      });
      field.disabled = this._emailPending;
      // Brief 020c — the honeypot. OFF-SCREEN, NOT `display:none`: a bot that
      // skips hidden fields is a bot this catches nothing on, and hiding by
      // position is what the plugin's design asks for. `tabindex="-1"` +
      // `aria-hidden` keep it off the keyboard path and out of the a11y tree, so
      // no real person can reach it by accident — a human who filled this in
      // would be silently dropped by the server.
      const hp = document.createElement("input");
      hp.type = "text";
      hp.className = "email-hp";
      hp.name = "hp";
      hp.tabIndex = -1;
      hp.autocomplete = "off";
      hp.setAttribute("aria-hidden", "true");
      hp.value = this._hpValue;
      hp.addEventListener("input", () => { this._hpValue = hp.value; });

      body.append(title, sub, field, hp);

      if (this._emailError) {
        const err = document.createElement("p");
        err.className = "email-error";
        err.textContent = t(EMAIL_ERROR_KEYS[this._emailError]);
        body.append(err);
      }

      const register = document.createElement("button");
      register.type = "button";
      register.className = "cta continue";
      // In flight the label says so and the button stops responding — a second
      // tap would spend another of this address's three links an hour.
      register.textContent = this._emailPending
        ? t("l2b.email.sending")
        : t("l2b.email.register");
      register.disabled = this._emailPending;
      register.addEventListener("click", () => this._onRegister());

      const skip = document.createElement("button");
      skip.type = "button";
      skip.className = "ghost";
      skip.textContent = t("l2b.email.skip");
      skip.addEventListener("click", () => this._onSkip());

      actions.append(register, skip);
    }

    // The registered state has no buttons, and an empty `.actions` would still
    // contribute its margin — so it is omitted rather than rendered hollow.
    const actionRow = actions.children.length ? actions : document.createTextNode("");

    return html`
      <section class="reveal">
        ${body}
        <div class="tail">
          ${actionRow}
          ${back}
        </div>
      </section>
    `;
  }

  /**
   * Brief 020b — one of the skipped ending's two outbound buttons. Same escape
   * hatch as `_continueCta`: a user-activated `<a target="_top">`, never a
   * programmatic `window.top.location` (a cross-origin iframe cannot do that —
   * Gotcha 3), so the browser performs the top-window navigation itself.
   *
   * It fires `continue_click` — the same 016 event the reveal's CTA used to
   * fire — because this IS the demo's exit conversion now that Continue
   * advances into the panel instead of leaving. Without it the Mandarin funnel
   * would simply stop reporting exits. `destination` stays HOST-ONLY, as 016
   * defines it: no path, no query, no PII.
   * @param {string} url
   * @param {string} label
   * @param {string} className
   * @returns {HTMLAnchorElement}
   */
  _exitLink(url, label, className) {
    const a = document.createElement("a");
    a.className = className;
    a.href = url;
    a.target = "_top";
    a.rel = "noopener";
    a.textContent = label;
    let destination = url;
    try { destination = new URL(url).host; } catch { /* keep the raw value */ }
    a.addEventListener("click", () => track("continue_click", { destination }));
    return a;
  }

  /**
   * Brief 020b — the reveal's Continue advances here. `email_view` is deduped
   * per RUN (`_emailViewTracked`), so walking back to the reveal and forward
   * again is still one panel view for one learner; a fresh `email_view` needs a
   * fresh run, which "Practice again" provides by clearing the flag.
   */
  _showEmailPanel() {
    this._emailState = "prompt";
    this._emailError = null;
    if (!this._emailViewTracked) {
      this._emailViewTracked = true;
      track("email_view");
    }
    this._scheduleRender();
  }

  /** Brief 020b — "See what you understood", from any of the three states. */
  _showReveal() {
    this._emailState = null;
    this._emailError = null;
    this._scheduleRender();
  }

  /**
   * Brief 020b + 020c — Register.
   *
   * The client-side sanity check is deliberately the weakest one that catches a
   * typo: an `@` with a dot after it. Anything stricter rejects real addresses,
   * and this is a one-shot capture from a stranger with no second chance to
   * correct it — a false rejection costs a registration, and the endpoint does
   * the real validation anyway (its 400 lands on the same prompt state).
   *
   * THEN IT WAITS FOR THE ANSWER. 020b advanced to "check your inbox"
   * regardless, because a no-cors POST had nothing to branch on. 020c reads the
   * response, so the pane the learner lands on is the one that is TRUE: the
   * inbox copy only over `ok`, and a fixable message over every failure. That is
   * the whole reason the plugin session built read-back rather than accepting
   * fire-and-forget — a typo that shows "check your inbox" is the demo lying to
   * the one person who just said yes.
   */
  async _onRegister() {
    if (this._emailPending) return; // one in-flight POST at a time
    const email = this._emailValue.trim();
    if (!this._looksLikeEmail(email)) {
      this._emailError = "invalid";
      this._scheduleRender();
      return;
    }
    // NO PII TO GA. `email_submit` is a bare count — the address goes to
    // `submitEmail` and nowhere else (analytics.js, house rule).
    track("email_submit");
    let langCode = "zh";
    try { langCode = lang.current().targetLanguage; } catch { /* keep the default */ }

    this._emailPending = true;
    this._emailError = null;
    this._scheduleRender();

    const result = await submitEmail(email, langCode, this._hpValue, {
      metCount: this._metCount(),
      masteredCount: this._masteredCount(),
    });
    this._emailPending = false;

    if (result.ok) {
      this._emailState = "registered";
      this._emailError = null;
      this._scheduleRender();
      return;
    }

    // `email_error` carries the REASON and nothing else (020c step 6): no
    // address, no server message, no status. It is the signal that makes typo
    // and throttle rates visible — the shape of failure that went unnoticed for
    // most of August because nothing counted it.
    track("email_error", { reason: result.reason });

    if (result.reason === "throttle" && result.code === "sg_waitlist_email_throttle") {
      // Not a failure to the learner: this address has had its links this hour,
      // so one is already sitting in their inbox. Its own ending, with its own
      // copy — "and check spam", because a link they never saw is the likeliest
      // reason they are standing here submitting again.
      this._emailState = "already";
      this._emailError = null;
      this._scheduleRender();
      return;
    }

    // Everything else stays on the prompt with the field intact, so the fix (or
    // the retry) is one tap away. `rejected` is the server's 400 — a different
    // sentence from the client-side `invalid`, because "we checked and that
    // address is not deliverable" is a stronger claim than "that doesn't look
    // right".
    this._emailError = result.reason === "invalid"
      ? "rejected"
      : result.reason === "throttle"
        ? "throttle"
        : "network";
    this._scheduleRender();
  }

  /** Brief 020b — Skip. Its own ending; never the inbox copy. */
  _onSkip() {
    track("email_skip");
    this._emailState = "skipped";
    this._emailError = null;
    this._scheduleRender();
  }

  /**
   * `@` with a dot somewhere after it, and something either side of both. Not
   * RFC 5322 — see `_onRegister` for why it must not be.
   * @param {string} value
   * @returns {boolean}
   */
  _looksLikeEmail(value) {
    const at = value.indexOf("@");
    if (at < 1 || at !== value.lastIndexOf("@")) return false;
    const domain = value.slice(at + 1);
    const dot = domain.indexOf(".");
    return dot > 0 && dot < domain.length - 1 && !/\s/.test(value);
  }

  // "Practice again": clear the persisted streaks for this item BEFORE re-init
  // so the rebuilt engine reads a cleared store, then re-run the connect path.
  _onPractiseAgain() {
    learner.clearStreaks(this._allItemMmuIds);
    if (this._feedbackTimer) { clearTimeout(this._feedbackTimer); this._feedbackTimer = null; }
    audio.stop();
    this._started = false;
    this._lastAudioConceptId = null;
    this._lastMode = null;
    this._largeIntroImageUrl = null;
    this._translationRevealed = false;
    // Brief 134: invalidate any in-flight tile pre-resolution and clear the map.
    this._prepareToken++;
    this._tileUrls = {};
    // Brief 016: a replay is a NEW lesson attempt — reset the lesson-scoped
    // analytics state so the next walk fires its own lesson_start /
    // item_mastered / lesson_complete rather than being deduped away.
    this._lessonCompleteTracked = false;
    this._attemptCount = 0;
    this._streaks.clear();
    this._masteredTracked.clear();
    // Brief 020: tear the reveal back down — including the host class that gives
    // the pane its height — so the replay returns to the training layout, and
    // clear the run-scoped abandon state so the next walk's `run_abandon` (if
    // the page never reloads) describes THAT walk. Brief 022: `_hideSeq` is NOT
    // reset — it counts hides per page LOAD, a replay is not a new load, and
    // restarting it would put two different `hide_seq: 1` rows in one session,
    // which is exactly what the resume-matching depends on not happening.
    this._revealMounted = false;
    this._revealTracked = false;
    // Brief 020b: a replay is a new run, so the panel goes back to unseen —
    // `_emailViewTracked` included, so the next walk's `email_view` belongs to
    // that walk. Same reasoning as `_hideSeq` above, opposite conclusion: that
    // one is per page LOAD and keeps counting; this one is per RUN.
    this._emailState = null;
    this._emailValue = "";
    this._emailError = null;
    this._hpValue = "";
    this._emailPending = false;
    this._emailViewTracked = false;
    // Brief 021: the card state is deliberately NOT reset. The card is fixed —
    // the same two sentences and the same four atoms for everyone, every run —
    // so a replay produces byte-identical bytes, and rebuilding them would only
    // buy a second `record_generate` for one record. `_cardPrep` holds decoded
    // images, not run state; it stays valid across the replay and `_prepareCard`
    // no-ops when the rebuilt reveal calls it again.
    this._revealUrlsToken++;
    this._revealUrls = {};
    this.classList.remove("l2b-reveal");
    this._preMastered = 0;
    this._metIds.clear();
    this._startedAt = 0;
    this._firstAttemptMs = null;
    this._audioOk = false;
    this._promptAudioPending = false;
    this.engine = null;
    this._scheduleRender();
    this.onConnected();
  }

  /** @param {import("../engines/l2b-engine.js").L2BItemData} target */
  async _resolveLargeIntroImage(target) {
    this._largeIntroImageUrl = null;
    const url = await resolveImage(target.mmu);
    if (this._lastAudioConceptId === target.conceptId) {
      this._largeIntroImageUrl = url ?? null;
      this._scheduleRender();
    }
  }

  /**
   * Brief 011: the completion "Continue" CTA — a user-activated `<a target="_top">`
   * to COMPLETION_URL that escapes the embed iframe into the SpeechGenie site.
   * The router ignores anchors whose target isn't "_self" (and the URL is cross-
   * origin anyway), so the browser performs the native top-window navigation.
   * Returns an empty node when COMPLETION_URL is unset, so callers fall back to
   * "practice again" alone.
   * @returns {Node}
   */
  _continueCta() {
    if (!COMPLETION_URL) return document.createTextNode("");
    const a = document.createElement("a");
    a.className = "cta continue";
    a.href = COMPLETION_URL;
    a.target = "_top";
    a.rel = "noopener";
    a.textContent = t("nav.continue");
    // Brief 016: the demo's exit conversion. Sent on the click that starts the
    // top-window navigation — GA4's transport keeps a beacon in flight across
    // an unload, and `track()` can't throw, so instrumenting here never risks
    // swallowing the navigation. `destination` is the HOST only (an enum-ish
    // value, no query string, no PII); a malformed URL degrades to the raw
    // constant rather than throwing.
    let destination = COMPLETION_URL;
    try { destination = new URL(COMPLETION_URL).host; } catch { /* keep the raw value */ }
    a.addEventListener("click", () => track("continue_click", { destination }));
    return a;
  }

  // Brief 003a: the lesson was already fully mastered on entry, so no engine was
  // built. Show a clear "already complete" state. "Practice again" reuses the
  // per-lesson streak-clear + re-init path (which, with streaks cleared, rebuilds
  // the engine on the full lesson).
  _renderAlreadyComplete() {
    const again = document.createElement("button");
    again.type = "button";
    again.className = "primary";
    again.textContent = t("l2b.practiceAgain");
    again.addEventListener("click", () => this._onPractiseAgain());
    const total = this._allItemMmuIds.length;
    return html`
      <section class="complete">
        <div class="headline">${t("l2b.alreadyComplete")}</div>
        <div class="detail">${total === 1 ? t("l2b.allMasteredOne") : t("l2b.allMastered", { n: total })}</div>
        <div class="actions">
          ${this._continueCta()}
          ${again}
        </div>
      </section>
    `;
  }

  /** @param {number} masteredCount */
  _renderCompletion(masteredCount) {
    const again = document.createElement("button");
    again.type = "button";
    again.className = "primary";
    again.textContent = t("l2b.practiceAgain");
    again.addEventListener("click", () => this._onPractiseAgain());
    // Brief 011: "Continue" (escape the iframe to SpeechGenie) is the primary exit;
    // "practice again" replays Lesson 1 as the fallback (and the only action if
    // COMPLETION_URL is unset).
    return html`
      <section class="complete">
        <div class="headline">${t("l2b.complete")}</div>
        <div class="detail">${masteredCount === 1 ? t("l2b.wordMastered", { n: masteredCount }) : t("l2b.wordsMastered", { n: masteredCount })}</div>
        <div class="actions">
          ${this._continueCta()}
          ${again}
        </div>
      </section>
    `;
  }

  render() {
    if (this._notFound) {
      return html`
        <section class="complete">
          <div class="headline">${t("l2b.notFound")}</div>
          <div class="actions">${this._continueCta()}</div>
        </section>
      `;
    }
    if (this._allMastered) {
      // Brief 020c: a returning visitor in the Mandarin direction gets the
      // reveal and the panel behind it (`onConnected` mounts them), so the
      // ending is the same one a fresh finisher sees. `_renderAlreadyComplete`
      // remains the English-direction ending, and the fallback when the reveal
      // is not eligible.
      if (this._revealMounted) {
        return this._emailState ? this._renderEmailPanel() : this._renderReveal();
      }
      return this._renderAlreadyComplete();
    }
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    if (!this._started) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.textContent = t("l2b.start");
      btn.addEventListener("click", () => this._handleStart());
      return html`
        <h2>${t("l2b.heading")}</h2>
        <div class="start-prompt">${btn}</div>
      `;
    }

    const s = this.engine.snapshot();
    const pct = s.totalItems > 0
      ? Math.round((s.masteredCount / s.totalItems) * 100)
      : 0;
    const progressRow = html`
      <div class="progress-row" role="progressbar"
           aria-valuemin="0" aria-valuemax="${s.totalItems}"
           aria-valuenow="${s.masteredCount}">
        <div class="progress-bar"><div class="progress-fill" style="width: ${pct}%"></div></div>
        <div class="progress">${s.masteredCount} / ${s.totalItems} mastered</div>
      </div>
    `;

    if (s.mode === "complete") {
      // Brief 020: the reveal replaces the Continue CTA — but only in the
      // Mandarin direction, and only once the mount edge in `onState` has
      // actually run (so a state published before that edge can never paint a
      // half-prepared pane). Everything else finishes as it does today.
      // Brief 020b: once the reveal has mounted, `_emailState` decides which
      // pane of the ending is on screen — the reveal itself (`null`) or one of
      // the panel's three states. Both live under `:host(.l2b-reveal)`, so the
      // host class is NOT touched here: the box keeps its height across the
      // whole ending and nothing reflows as the learner moves through it.
      if (this._revealMounted) {
        return this._emailState ? this._renderEmailPanel() : this._renderReveal();
      }
      return this._renderCompletion(s.masteredCount);
    }

    if (s.mode === "largeIntro" && s.introItem) {
      const advance = document.createElement("button");
      advance.type = "button";
      advance.className = "primary";
      advance.textContent = "▶";
      advance.addEventListener("click", () => this._handleAdvance());

      const replay = document.createElement("button");
      replay.type = "button";
      replay.className = "replay";
      replay.textContent = `🔊 ${t("audio.replay")}`;
      replay.addEventListener("click", () => this._handleReplay());

      // The word shown is the SELECTED target's rendering (Brief 004b): English
      // `phrase` or Mandarin `phraseZh`, never hardcoded to the engine's English
      // displayText.
      const introText = lang.targetText(s.introItem.mmu);
      const img = this._largeIntroImageUrl
        ? Object.assign(document.createElement("img"), {
            src: this._largeIntroImageUrl,
            alt: introText,
            className: "l2b-image-container",
          })
        : document.createTextNode("");

      // Translation aid (Brief 006f): hidden behind an "i" info button. By
      // default no L1 word is shown — only the ⓘ. Tapping ⓘ reveals the L1 word
      // AND plays the L1 audio (Gotcha 1); re-tapping the revealed pill replays.
      // Suppressed entirely when L1 === target (nothing to translate — Gotcha 4).
      const intro = s.introItem;
      const translationText = lang.scaffoldText(intro.mmu);
      let translation;
      if (!translationText) {
        translation = document.createTextNode("");
      } else if (this._translationRevealed) {
        // Revealed: the L1 word as a replay pill (plays L1 audio on tap).
        translation = Object.assign(document.createElement("button"), {
          type: "button",
          className: "replay translation",
          textContent: `🔊 ${translationText}`,
        });
        translation.setAttribute("aria-label", `${t("l2b.hearTranslation")}: ${translationText}`);
        translation.addEventListener("click", () => this._playL1Audio(intro));
      } else {
        // Hidden: only the ⓘ. Tap reveals the word + plays the L1 audio.
        translation = Object.assign(document.createElement("button"), {
          type: "button",
          className: "translation-info",
          textContent: "ⓘ",
        });
        translation.setAttribute("aria-label", t("l2b.hearTranslation"));
        translation.addEventListener("click", () => {
          this._translationRevealed = true;
          // Brief 016: "the learner needed the translation". Fired on the
          // reveal tap only — the revealed pill's replay taps are a different
          // (untracked) affordance. No word/PII, just the fact of the reveal.
          track("info_reveal");
          this._playL1Audio(intro);
          this._scheduleRender();
        });
      }

      return html`
        ${progressRow}
        <h2>${t("l2b.heading")}</h2>
        <div class="large-intro">
          ${img}
          ${this._renderFlags.showText
            ? html`<div class="word">${introText}</div>`
            : html``}
          ${translation}
          <div>${replay} ${advance}</div>
        </div>
      `;
    }

    // tileGrid or feedback
    const target = s.targetItem;
    if (!target) return html`<p>${t("loading")}</p>`;

    const dragArea = document.createElement("kfe-l2b-drag-area");
    /** @type {any} */ (dragArea).items = s.tiles.map((d) => d.mmu);
    // Brief 134: hand the pre-resolved tile URLs to the drag-area so it paints
    // images on its first frame rather than resolving them JIT (blank window).
    /** @type {any} */ (dragArea).urls = this._tileUrls;
    /** @type {any} */ (dragArea).disabled = s.mode === "feedback";
    dragArea.addEventListener("pick", (e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      this._handlePick(String(detail.mmuId));
    });
    // Brief 016 (optional event): tap-to-enlarge. The drag-area already emits
    // `kfe:tile-preview` for its own view affordance (Brief 051) — this is a
    // listener on it, so the UI component stays analytics-free. The emitted
    // mmuId is deliberately NOT forwarded (no PII).
    dragArea.addEventListener("kfe:tile-preview", () => track("tile_enlarge"));

    const replay = document.createElement("button");
    replay.type = "button";
    replay.className = "replay";
    replay.textContent = `🔊 ${t("audio.replay")}`;
    replay.addEventListener("click", () => this._handleReplay());

    const fbBlock = s.mode === "feedback"
      ? html`<div class="feedback ${s.wasCorrect ? "correct" : "wrong"}">
          ${s.wasCorrect ? "✓" : "✗"}
        </div>`
      : html`<div class="feedback idle" aria-hidden="true"></div>`;

    return html`
      ${progressRow}
      <h2>${t("l2b.heading")}</h2>
      <div class="target">
        ${this._renderFlags.showText
          ? html`<span class="word">${lang.targetText(target.mmu)}</span>`
          : html`<span class="word" aria-hidden="true"></span>`}
        ${replay}
      </div>
      ${fbBlock}
      <div class="grid">${dragArea}</div>
    `;
  }
}
